import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineButton,
  PdsMantineIconButton,
  PdsLoader,
  PdsNotification,
  PdsTag,
  PdsModal,
  PdsModalHeader,
  PdsModalBody,
  PdsModalFooter,
  PdsTextField,
  PdsTextArea,
  PdsLink,
  NativeSelect,
  PdsArrowLeftIcon,
  PdsEditIcon,
  PdsLinkIcon,
  PdsSearchIcon,
  PdsSyncIcon,
  PdsPlusAddIcon,
  PdsSpinnerIcon,
  PdsChevronUpSmIcon,
  PdsChevronDownSmIcon,
  PdsChevronRightSmIcon,
  PdsTab,
  PdsDocument1Icon,
  PdsDocumentDownloadIcon,
  PdsHistoryIcon,
  PdsServerIcon,
  PdsMonitorDesktopIcon,
  PdsNetworkIcon,
  PdsEmailIcon,
  PdsSitemapIcon,
  PdsGlobe2Icon,
  PdsWriteEditArticleIcon,
  PdsShieldSecurityIcon,
  PdsTrashDeleteIcon,
  PdsExternalLinkIcon,
  PdsCheckConfirmIcon,
  PdsStopIcon,
  PdsTicketIcon,
  PdsFilterIcon,
} from '@podium-design-system/react-components';
import CaseEditModal from '../components/CaseEditModal';
import InvestigationModal from '../components/InvestigationModal';
import InvestigationExportModal from '../components/InvestigationExportModal';
import CaseWorkflowMaskOverlay from '../components/CaseWorkflowMaskOverlay';
import InvestigationNetworkFlow from '../components/InvestigationNetworkFlow';
import InvestigationSectionGrid from '../components/InvestigationSectionGrid';
import InvestigatePairButtons from '../components/InvestigatePairButtons';
import CrossCaseLinkageRecord from '../components/CrossCaseLinkageRecord';
import UrlScanInformation from '../components/UrlScanInformation';
import SecurityTrailsHistoricalPanel from '../components/investigation/SecurityTrailsHistoricalPanel';
import Pagination from '../components/Pagination';
import { InvestigationDensityToolbar, InvestigationSectionCard, InvestigationDenseRow } from '../components/InvestigationExpandable';
import { textMatchesKeywordFilters, objectMatchesKeywordFilters, isKeywordQueryActive, emptyKeywordQuery } from '../utils/resultKeywordFilter';
import { destinationForToolSourceKind, scheduleScrollAndHighlight } from '../utils/toolSourceKindJump';
import { formatPublicWwwRank, sortPublicWwwByRankAsc } from '../utils/publicWwwDisplay';
import {
  filterARecords,
  filterAaaaRecords,
  buildDomainsOnIpFilterOptions,
  filterDomainsOnIpBySiblingIp,
  buildDomainsOnNsFilterOptions,
  filterDomainsOnNsByNameserver,
  soaPrimaryNameserver,
  soaAdminMailbox,
  soaDenseTitleLine,
} from '../utils/dnsRecordType';
import { sortCertificatesByFirstSeen } from '../utils/certificateSort';
import { SECURITY_TRAILS_CARD_EDGE_LABEL, PUBLICWWW_EDGE_LABEL } from '../utils/investigationEdgeLabels';
import { connectionLabelFromFindingRow, resolveFindingCategoryHeading, FINDING_TYPE_EDGE_LABEL } from '../utils/findingLabels';
import { useAuth } from '../contexts/AuthContext';
import { isPlausibleInvestigateDomain } from '../utils/investigateDomain';
import { displayUsername } from '../utils/displayUser';
import { canShowAddFindingToMap } from '../utils/findingMapActions';
import {
  enabledWorkflowToolsPending,
  allInvestigationsTerminal,
  investigationBranchWorkflowBusy,
  isInvestigationTerminalStatus,
  pickBusyInvestigationIdForMask,
} from '../utils/investigationWorkflowMask';

const LEGACY_NOTES_AUTHOR_ID = '__legacy_case_notes__';
const NOTE_REACTION_EMOJIS = ['👍', '✅'];

function displayNameForNoteAuthor(authorUserId) {
  if (authorUserId === LEGACY_NOTES_AUTHOR_ID) return 'Imported from case notes';
  if (!authorUserId) return 'Unknown';
  return displayUsername(authorUserId) || authorUserId;
}

/** Single-line author for note cards. */
function formatNoteAuthorLine(authorUserId) {
  return displayNameForNoteAuthor(authorUserId);
}

function formatNoteReactionTooltip(reactorUserIds, canToggle) {
  const ids = Array.isArray(reactorUserIds) ? reactorUserIds.filter(Boolean) : [];
  const names = ids.map((id) => displayUsername(id) || id);
  const action = canToggle ? 'Click to toggle your reaction.' : 'Sign in to react.';
  if (names.length === 0) return action;
  return `Reacted by: ${names.join(', ')}. ${action}`;
}

function formatNoteListDate(dateString) {
  if (dateString == null || dateString === '') return '—';
  const d = new Date(dateString);
  return Number.isNaN(d.getTime()) ? String(dateString) : d.toLocaleDateString();
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Whether an IP geolocation record has analyst-visible geo or ASN (compact rows expand by default when true).
 */
function ipGeoHasDisplayableGeoOrAsn(geo) {
  if (!geo || typeof geo !== 'object') return false;
  const s = (v) => v != null && String(v).trim() !== '';
  const hasAsn = s(geo.asn) || s(geo.asnOrg) || s(geo.asn_org);
  const hasGeo =
    s(geo.city) ||
    s(geo.country) ||
    s(geo.region) ||
    s(geo.countryCode) ||
    s(geo.postalCode) ||
    (geo.latitude != null &&
      geo.longitude != null &&
      !Number.isNaN(Number(geo.latitude)) &&
      !Number.isNaN(Number(geo.longitude)));
  return hasAsn || hasGeo;
}

/** Per–SecurityTrails-card text filter (substring), applied after global tab search terms. */
function securityTrailsCardTextMatch(needle, haystack) {
  const n = String(needle ?? '').trim().toLowerCase();
  if (!n) return true;
  return String(haystack ?? '').toLowerCase().includes(n);
}

function domainsListMetadataSearchText(row) {
  return [row.hostingProvider, row.mailProvider]
    .filter((value) => value != null && String(value).trim() !== '')
    .join(' ');
}

function DomainsListMetadataFields({ row }) {
  return (
    <PdsBox direction="row" alignItems="baseline" gap="md" flexWrap="wrap">
      <PdsBox direction="row" alignItems="baseline" gap="xs">
        <PdsMantineText type="textline" component="span" className="argus-type-label">
          Hosting:
        </PdsMantineText>
        <PdsMantineText type="textline" component="span" className="argus-data-sm">
          {row.hostingProvider || '—'}
        </PdsMantineText>
      </PdsBox>
      <PdsBox direction="row" alignItems="baseline" gap="xs">
        <PdsMantineText type="textline" component="span" className="argus-type-label">
          Mail:
        </PdsMantineText>
        <PdsMantineText type="textline" component="span" className="argus-data-sm">
          {row.mailProvider || '—'}
        </PdsMantineText>
      </PdsBox>
    </PdsBox>
  );
}

function toolOutcomeByKind(outcomes, kind) {
  if (!Array.isArray(outcomes)) return null;
  return outcomes.find((o) => o.tool === kind) || null;
}

function InvestigationToolOutcomeBanner({ outcome, streamingPreview = false }) {
  if (!outcome || outcome.status === 'OK') return null;
  const color = outcome.status === 'TIMEOUT' || outcome.status === 'ERROR' ? 'warning' : 'neutral';
  let message = outcome.message;
  if (streamingPreview && outcome.status === 'EMPTY') {
    message = `${outcome.message} This only reflects this source so far — other preview steps may still be running; use the progress panel above.`;
  }
  return (
    <PdsNotification color={color} dismissible={false} style={{ marginBottom: 8 }}>
      {message}
    </PdsNotification>
  );
}

function CrtShRerunPrompt({ onRerun, rerunning }) {
  return (
    <PdsBox direction="column" gap="sm" alignItems="flex-start">
      <PdsMantineText type="textline" color="neutral" style={{ lineHeight: 1.55, margin: 0 }} component="p">
        Sometimes crt.sh fails to return results. In such cases, please click here to re-run Crt.sh
      </PdsMantineText>
      <PdsMantineButton
        rank="outline"
        color="action"
        size="sm"
        onClick={onRerun}
        disabled={rerunning}
        leadingIcon={rerunning ? <PdsSpinnerIcon /> : <PdsSyncIcon />}
      >
        {rerunning ? 'Re-running…' : 'RE-RUN'}
      </PdsMantineButton>
    </PdsBox>
  );
}

const PublicWwwSnippetHighlight = ({ snippet, searchTerm }) => {
  if (!snippet) return null;
  if (!searchTerm?.trim()) return <span style={{ wordBreak: 'break-word' }}>{snippet}</span>;
  const parts = snippet.split(new RegExp(`(${escapeRegExp(searchTerm.trim())})`, 'gi'));
  return (
    <span style={{ wordBreak: 'break-word' }}>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.trim().toLowerCase() ? <strong key={i}>{part}</strong> : part,
      )}
    </span>
  );
};

function hostnameOrIpFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    return new URL(url).hostname || '';
  } catch {
    return '';
  }
}

/** Home preview: PublicWWW CSV rows (rank, URL, snippet). */
const PublicWwwPreviewTable = ({ rows, searchTerm, onPreviewContinue }) => {
  const showContinue = typeof onPreviewContinue === 'function';
  return (
    <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
      <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-card-title">
        PublicWWW website matches ({rows.length})
      </PdsMantineText>
      <div style={{ overflowX: 'auto', border: '1px solid #d0d7de', borderRadius: 8 }}>
        <table className="argus-table-investigation" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>Rank</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>Url</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>Snippets</th>
              {showContinue && (
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', width: 120 }}> </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const pivot = hostnameOrIpFromUrl(row.url) || row.url;
              return (
                <tr key={`${row.rank}-${idx}`} style={{ verticalAlign: 'top' }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', fontVariantNumeric: 'tabular-nums' }}>
                    {formatPublicWwwRank(row.rank)}
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                    <PdsLink href={row.url} target="_blank" rel="noopener noreferrer" className="argus-data">
                      {row.url}
                    </PdsLink>
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }} className="argus-type-row-meta">
                    <PublicWwwSnippetHighlight snippet={row.snippet} searchTerm={searchTerm} />
                  </td>
                  {showContinue && (
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                      {pivot ? (
                        <InvestigatePairButtons
                          targetDomain={pivot}
                          deriveFrom="PUBLICWWW_WEBSITE"
                          connectionLabel={PUBLICWWW_EDGE_LABEL}
                          onRunTools={onPreviewContinue}
                          showAddNode={false}
                        />
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PdsBox>
  );
};

const truncateUrl = (url, maxLength = 100) => {
  if (!url || url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
};

const STATUS_COLOR = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
};

// ─── CaseDetails ─────────────────────────────────────────────────────────────

const CaseDetails = () => {
  const { caseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState(null);
  /** One-shot graph viewport jump after home keyword search "View node →"; cleared when InvestigationNetworkFlow finishes fitView. */
  const [homeNavFitViewNodeId, setHomeNavFitViewNodeId] = useState(null);
  /** Captured before location.state is cleared so Investigation Data can open the matched section. */
  const [homeJump, setHomeJump] = useState(null);
  const onFitViewToTargetConsumed = useCallback(() => {
    setHomeNavFitViewNodeId(null);
  }, []);
  /** Bumped when user clicks findings count on chart so Investigation Data switches to the Findings tab. */
  const [findingsTabNavigateNonce, setFindingsTabNavigateNonce] = useState(0);
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [scanLinkageBusy, setScanLinkageBusy] = useState(false);
  const [linkageScanMessage, setLinkageScanMessage] = useState(null);
  const [linkageScanError, setLinkageScanError] = useState(null);
  const [linkageRefreshNonce, setLinkageRefreshNonce] = useState(0);

  const [caseNotes, setCaseNotes] = useState([]);
  const [caseNotesLoading, setCaseNotesLoading] = useState(false);
  const [caseNotesError, setCaseNotesError] = useState(null);
  const [caseNotesSuccess, setCaseNotesSuccess] = useState(null);
  const [newCaseNoteBody, setNewCaseNoteBody] = useState('');
  const [postingCaseNote, setPostingCaseNote] = useState(false);
  const [editingCaseNoteId, setEditingCaseNoteId] = useState(null);
  const [editCaseNoteDraft, setEditCaseNoteDraft] = useState('');
  const [savingCaseNoteId, setSavingCaseNoteId] = useState(null);
  const [deletingCaseNoteId, setDeletingCaseNoteId] = useState(null);
  const [caseNoteReactionBusyId, setCaseNoteReactionBusyId] = useState(null);
  /** Top-level case view: investigation tools vs shared case notes. */
  const [caseSubPage, setCaseSubPage] = useState('details');
  /** Polled GET /details while workflow mask is visible (tool checklist + workflow flags). */
  const [maskDetailsSnapshot, setMaskDetailsSnapshot] = useState(null);
  /** Tool flags from Investigate modal until GET /details persists workflowRequestedTools (PENDING gap). */
  const [pendingMaskWorkflowTools, setPendingMaskWorkflowTools] = useState(null);
  /** User bypass when workflow HUD blocks the case page (stuck RUNNING / slow ingest). */
  const [workflowMaskDismissed, setWorkflowMaskDismissed] = useState(false);

  const rootInvestigationId = useMemo(
    () => investigations.find((inv) => !inv.parentInvestigationId)?.id ?? null,
    [investigations],
  );

  const awaitingCaseIngest = Boolean(location.state?.awaitingCaseIngest);
  const rootInvestigation = useMemo(
    () => investigations.find((inv) => !inv.parentInvestigationId) ?? null,
    [investigations],
  );
  const selectedInvestigation = useMemo(
    () => (selectedInvestigationId ? investigations.find((inv) => inv.id === selectedInvestigationId) ?? null : null),
    [investigations, selectedInvestigationId],
  );
  /** Branch anchor for mask: selected chart node, else root (never null when investigations exist). */
  const maskBranchAnchorId = useMemo(() => {
    if (selectedInvestigationId) return selectedInvestigationId;
    return rootInvestigation?.id ?? null;
  }, [selectedInvestigationId, rootInvestigation?.id]);

  /** Investigation whose /details feed drives the HUD (deepest RUNNING on branch, etc.). */
  const maskPollInvestigationId = useMemo(() => {
    if (!maskBranchAnchorId || (awaitingCaseIngest && investigations.length === 0)) return null;
    return pickBusyInvestigationIdForMask(investigations, maskBranchAnchorId);
  }, [investigations, maskBranchAnchorId, awaitingCaseIngest]);

  /**
   * Blocking overlay while any node on the selected branch is PENDING/RUNNING, or first ingest before list exists.
   * (Parent COMPLETED + child RUNNING still masks.)
   */
  const investigationWorkflowBusy = useMemo(() => {
    if (awaitingCaseIngest && investigations.length === 0) return true;
    if (!rootInvestigation) return Boolean(awaitingCaseIngest);
    if (!maskBranchAnchorId) return Boolean(awaitingCaseIngest);
    if (investigationBranchWorkflowBusy(investigations, maskBranchAnchorId)) {
      const flags = maskDetailsSnapshot?.workflowRequestedTools;
      const outcomes = maskDetailsSnapshot?.toolOutcomes;
      if (flags && outcomes && maskPollInvestigationId
          && !enabledWorkflowToolsPending(flags, outcomes, 'RUNNING')) {
        return false;
      }
      return true;
    }
    if (awaitingCaseIngest) {
      return !isInvestigationTerminalStatus(rootInvestigation.status);
    }
    return false;
  }, [awaitingCaseIngest, investigations, rootInvestigation, maskBranchAnchorId, maskDetailsSnapshot, maskPollInvestigationId]);

  const gracePollInvIdRef = useRef(null);
  const [workflowMaskGrace, setWorkflowMaskGrace] = useState(false);
  const [gracePollInvestigationId, setGracePollInvestigationId] = useState(null);
  /** Covers the gap between rerun POST returning and investigation list polling RUNNING (backend commits RUNNING before async worker). */
  const [rerunWorkflowLatch, setRerunWorkflowLatch] = useState(false);
  const prevWorkflowBusyForGraceRef = useRef(investigationWorkflowBusy);

  useEffect(() => {
    if (maskPollInvestigationId) {
      gracePollInvIdRef.current = maskPollInvestigationId;
    }
  }, [maskPollInvestigationId]);

  useEffect(() => {
    const prev = prevWorkflowBusyForGraceRef.current;
    prevWorkflowBusyForGraceRef.current = investigationWorkflowBusy;
    if (prev && !investigationWorkflowBusy) {
      const gid = gracePollInvIdRef.current;
      if (gid) {
        setGracePollInvestigationId(gid);
        setWorkflowMaskGrace(true);
        const tid = setTimeout(() => {
          setWorkflowMaskGrace(false);
          setGracePollInvestigationId(null);
        }, 1800);
        return () => clearTimeout(tid);
      }
    }
    if (investigationWorkflowBusy) {
      setWorkflowMaskGrace(false);
      setGracePollInvestigationId(null);
    }
    return undefined;
  }, [investigationWorkflowBusy]);

  const maskHudTargetInvestigation = useMemo(() => {
    const pollId = maskPollInvestigationId ?? gracePollInvestigationId;
    if (pollId) {
      return investigations.find((inv) => inv.id === pollId) ?? null;
    }
    return selectedInvestigation ?? rootInvestigation;
  }, [investigations, maskPollInvestigationId, gracePollInvestigationId, selectedInvestigation, rootInvestigation]);

  const caseAllInvestigationsTerminal = useMemo(
    () => allInvestigationsTerminal(investigations),
    [investigations],
  );

  const workflowToolsPending = useMemo(() => {
    if (caseAllInvestigationsTerminal) return false;
    const flags = maskDetailsSnapshot?.workflowRequestedTools ?? pendingMaskWorkflowTools;
    if (!flags) return false;
    const statusSource =
      maskHudTargetInvestigation?.status
      ?? maskDetailsSnapshot?.status
      ?? investigations.find((inv) => inv.id === maskBranchAnchorId)?.status;
    return enabledWorkflowToolsPending(flags, maskDetailsSnapshot?.toolOutcomes, statusSource);
  }, [
    caseAllInvestigationsTerminal,
    maskDetailsSnapshot,
    pendingMaskWorkflowTools,
    maskHudTargetInvestigation?.status,
    maskBranchAnchorId,
    investigations,
  ]);

  const workflowMaskVisible =
    !workflowMaskDismissed
    && (investigationWorkflowBusy || workflowMaskGrace || rerunWorkflowLatch || workflowToolsPending);

  /** HUD /details poll target — avoid pushing snapshot into a different selected node. */
  const workflowHudPollInvestigationId =
    maskPollInvestigationId ?? gracePollInvestigationId ?? null;
  const workflowDetailsSnapshotForSelected = useMemo(() => {
    if (!maskDetailsSnapshot || !selectedInvestigationId || !workflowHudPollInvestigationId) {
      return null;
    }
    if (workflowHudPollInvestigationId !== selectedInvestigationId) {
      return null;
    }
    return maskDetailsSnapshot;
  }, [maskDetailsSnapshot, selectedInvestigationId, workflowHudPollInvestigationId]);

  useEffect(() => {
    if (investigationWorkflowBusy) {
      setRerunWorkflowLatch(false);
    }
  }, [investigationWorkflowBusy]);

  useEffect(() => {
    if (!rerunWorkflowLatch) return undefined;
    const t = window.setTimeout(() => setRerunWorkflowLatch(false), 15000);
    return () => window.clearTimeout(t);
  }, [rerunWorkflowLatch]);

  useEffect(() => {
    setWorkflowMaskDismissed(false);
  }, [caseId]);

  useEffect(() => {
    if (!investigationWorkflowBusy && !workflowMaskGrace && !rerunWorkflowLatch && !workflowToolsPending) {
      setWorkflowMaskDismissed(false);
    }
  }, [investigationWorkflowBusy, workflowMaskGrace, rerunWorkflowLatch, workflowToolsPending]);

  /** Poll investigation list while any node (root or child) is still ingesting — root-only polling left child nodes stuck on PENDING after the parent finished. */
  const shouldPollInvestigations = useMemo(() => {
    if (rerunWorkflowLatch) return true;
    if (awaitingCaseIngest && investigations.length === 0) return true;
    return investigations.some((inv) => {
      const s = String(inv.status || '').toUpperCase();
      return s === 'PENDING' || s === 'RUNNING';
    });
  }, [rerunWorkflowLatch, awaitingCaseIngest, investigations]);

  const markInvestigationRunningLocally = useCallback((invId) => {
    if (!invId) return;
    const idStr = String(invId);
    setInvestigations((prev) =>
      prev.map((inv) =>
        String(inv.id) === idStr ? { ...inv, status: 'RUNNING', completedAt: null } : inv,
      ),
    );
    setRerunWorkflowLatch(true);
  }, []);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await axios.put(`/api/cases/${caseId}/status`, { status: newStatus });
      setCaseData(res.data);
    } catch (e) {
      setActionError('Failed to update case status: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDeleteCase = async () => {
    if (!window.confirm(`Are you sure you want to delete the case for "${caseData.target}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`/api/cases/${caseId}`);
      window.location.href = '/cases';
    } catch (e) {
      setActionError('Failed to delete case: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleScanLinkage = async () => {
    setScanLinkageBusy(true);
    setLinkageScanMessage(null);
    setLinkageScanError(null);
    try {
      await axios.post(`/api/cases/${caseId}/scan-linkage`);
      setLinkageScanMessage('Cross-case linkage scan completed. Graph badges have been refreshed.');
      setLinkageRefreshNonce((n) => n + 1);
    } catch (e) {
      setLinkageScanError('Failed to scan linkage: ' + (e.response?.data?.message || e.message));
    } finally {
      setScanLinkageBusy(false);
    }
  };

  const handleDeleteInvestigation = async (deletedInvestigationId) => {
    // Reload investigation list from backend after deletion
    try {
      const invs = await axios.get(`/api/investigations/case/${caseId}`);
      const investigationsData = Array.isArray(invs.data) ? invs.data : [];
      setInvestigations(investigationsData);
      // If the deleted investigation was selected, fall back to root
      if (selectedInvestigationId === deletedInvestigationId) {
        const root = investigationsData.find(inv => !inv.parentInvestigationId);
        setSelectedInvestigationId(root ? root.id : null);
      }
    } catch (e) {
      setActionError('Failed to refresh nodes: ' + (e.response?.data?.message || e.message));
    }
  };

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.pageYOffset > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setInvestigations([]);
        setHomeNavFitViewNodeId(null);

        const c = await axios.get(`/api/cases/${caseId}`);
        setCaseData(c.data);
        setLoading(false);

        const invs = await axios.get(`/api/investigations/case/${caseId}`);
        const investigationsData = Array.isArray(invs.data) ? invs.data : [];
        setInvestigations(investigationsData);

        // Navigation from home search panel: honour selectedInvestigationId from state, else fall back to root
        const navInvId = location.state?.selectedInvestigationId;
        const navJumpFinding = location.state?.jumpToFindingId;
        const navSourceKind = location.state?.jumpToSourceKind || null;
        const navNeedle = location.state?.jumpToNeedle || null;
        const navSubPage = location.state?.caseSubPage;

        const rootInvestigation = investigationsData.find(inv => !inv.parentInvestigationId);
        const targetInvId = navInvId && investigationsData.some(inv => inv.id === navInvId)
          ? navInvId
          : rootInvestigation?.id ?? null;

        if (targetInvId) setSelectedInvestigationId(targetInvId);

        if (navInvId && investigationsData.some((inv) => inv.id === navInvId)) {
          setHomeNavFitViewNodeId(navInvId);
        } else {
          setHomeNavFitViewNodeId(null);
        }

        if (navSubPage) {
          setCaseSubPage(navSubPage);
        }

        if (navInvId || navJumpFinding || navSourceKind) {
          setHomeJump({
            investigationId: navInvId || null,
            findingId: navJumpFinding || null,
            sourceKind: navSourceKind,
            needle: navNeedle,
          });
        } else {
          setHomeJump(null);
        }

        if (navJumpFinding) {
          // Switch to findings tab via nonce; scroll handled in InvestigationData when jumpToFindingId is set
          setFindingsTabNavigateNonce((n) => n + 1);
        }

        // Clear navigation state so refreshes don't re-apply it (drop awaitingCaseIngest once case is terminal)
        const allTerminal = allInvestigationsTerminal(investigationsData);
        const preserveAwaiting = Boolean(location.state?.awaitingCaseIngest) && !allTerminal;
        navigate(
          `/cases/${caseId}`,
          { replace: true, state: preserveAwaiting ? { awaitingCaseIngest: true } : {} },
        );
      } catch (e) {
        console.error(e);
        setError('Failed to load case details');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Drop Save-Case navigation flag once the root investigation has finished (or failed).
  useEffect(() => {
    if (loading || !awaitingCaseIngest) return;
    const root = investigations.find((inv) => !inv.parentInvestigationId);
    const st = root ? String(root.status || '').toUpperCase() : '';
    if (!root || (st !== 'COMPLETED' && st !== 'FAILED')) return;
    const flags = maskDetailsSnapshot?.workflowRequestedTools ?? pendingMaskWorkflowTools;
    if (flags && enabledWorkflowToolsPending(flags, maskDetailsSnapshot?.toolOutcomes, root.status)) {
      return;
    }
    navigate(`/cases/${caseId}`, { replace: true, state: {} });
  }, [loading, awaitingCaseIngest, investigations, caseId, navigate, maskDetailsSnapshot, pendingMaskWorkflowTools]);

  // Poll while any investigation in the case is still PENDING/RUNNING so child nodes update when the root is already COMPLETED.
  useEffect(() => {
    if (loading || !caseId || !shouldPollInvestigations) return undefined;

    const tick = async () => {
      try {
        const invs = await axios.get(`/api/investigations/case/${caseId}`);
        const data = Array.isArray(invs.data) ? invs.data : [];
        setInvestigations(data);
        setSelectedInvestigationId((prev) => {
          if (prev && data.some((i) => i.id === prev)) return prev;
          const r = data.find((inv) => !inv.parentInvestigationId);
          return r ? r.id : prev;
        });
      } catch (e) {
        console.error(e);
      }
    };

    const id = setInterval(tick, 2500);
    tick();
    return () => clearInterval(id);
  }, [loading, caseId, shouldPollInvestigations]);

  useEffect(() => {
    if (!investigationWorkflowBusy && !workflowMaskGrace && !rerunWorkflowLatch && !workflowToolsPending) {
      setMaskDetailsSnapshot(null);
    }
  }, [investigationWorkflowBusy, workflowMaskGrace, rerunWorkflowLatch, workflowToolsPending]);

  useEffect(() => {
    const hudPollActive =
      (investigationWorkflowBusy ||
        workflowMaskGrace ||
        rerunWorkflowLatch) &&
      !(awaitingCaseIngest && investigations.length === 0);
    if (!hudPollActive) {
      return undefined;
    }
    let pollId = investigationWorkflowBusy ? maskPollInvestigationId : gracePollInvestigationId;
    if (!pollId && rerunWorkflowLatch && maskBranchAnchorId) {
      pollId = maskBranchAnchorId;
    }
    if (!pollId) {
      return undefined;
    }
    const tick = async () => {
      try {
        const res = await axios.get(`/api/investigations/${pollId}/workflow-snapshot`);
        setMaskDetailsSnapshot(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    tick();
    const pollMs = investigationWorkflowBusy ? 5000 : 2000;
    const timer = setInterval(tick, pollMs);
    return () => clearInterval(timer);
  }, [
    investigationWorkflowBusy,
    workflowMaskGrace,
    rerunWorkflowLatch,
    gracePollInvestigationId,
    maskPollInvestigationId,
    maskBranchAnchorId,
    awaitingCaseIngest,
    investigations.length,
  ]);

  useEffect(() => {
    if (!investigationWorkflowBusy && !workflowMaskGrace && !rerunWorkflowLatch && !workflowToolsPending) {
      setPendingMaskWorkflowTools(null);
    }
  }, [investigationWorkflowBusy, workflowMaskGrace, rerunWorkflowLatch, workflowToolsPending]);

  useEffect(() => {
    if (maskDetailsSnapshot?.workflowRequestedTools != null) {
      setPendingMaskWorkflowTools(null);
    }
  }, [maskDetailsSnapshot?.workflowRequestedTools]);

  useEffect(() => {
    if (!pendingMaskWorkflowTools || !maskBranchAnchorId) return;
    if (investigationBranchWorkflowBusy(investigations, maskBranchAnchorId)) return;
    const anchor = investigations.find((inv) => inv.id === maskBranchAnchorId);
    const st = String(anchor?.status || '').toUpperCase();
    if (st !== 'COMPLETED' && st !== 'FAILED') return;
    if (enabledWorkflowToolsPending(pendingMaskWorkflowTools, maskDetailsSnapshot?.toolOutcomes, anchor?.status)) {
      return;
    }
    setPendingMaskWorkflowTools(null);
  }, [investigations, pendingMaskWorkflowTools, maskBranchAnchorId, maskDetailsSnapshot?.toolOutcomes]);

  useEffect(() => {
    if (!caseId || investigations.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setCaseNotesLoading(true);
        setCaseNotesError(null);
        const res = await axios.get(`/api/investigations/case/${caseId}/notes`);
        if (!cancelled) setCaseNotes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          setCaseNotesError('Failed to load notes: ' + (e.response?.data?.message || e.message));
        }
      } finally {
        if (!cancelled) setCaseNotesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, investigations.length]);

  useEffect(() => {
    // Only reset subpage when navigating to a different case without a jump target.
    if (!location.state?.caseSubPage) {
      setCaseSubPage('details');
    }
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to a specific note after notes have loaded (from home search panel navigation).
  const jumpToNoteIdRef = React.useRef(location.state?.jumpToNoteId ?? null);
  useEffect(() => {
    const noteId = jumpToNoteIdRef.current;
    if (!noteId || caseNotesLoading || caseNotes.length === 0) return;
    jumpToNoteIdRef.current = null;
    const tid = setTimeout(() => {
      const el = document.querySelector(`[data-note-id="${noteId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.outline = '2px solid #f59e0b';
        el.style.borderRadius = '8px';
        setTimeout(() => { el.style.outline = ''; el.style.borderRadius = ''; }, 2500);
      }
    }, 400);
    return () => clearTimeout(tid);
  }, [caseNotesLoading, caseNotes.length]);

  const mergeCaseNoteInList = (updated) => {
    if (!updated?.id) return;
    setCaseNotes((prev) => {
      const next = prev.map((n) => (n.id === updated.id ? { ...n, ...updated } : n));
      return [...next].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    });
  };

  const handlePostCaseNote = async () => {
    if (!rootInvestigationId) return;
    const body = newCaseNoteBody.trim();
    if (!body) return;
    try {
      setPostingCaseNote(true);
      setCaseNotesError(null);
      setCaseNotesSuccess(null);
      const res = await axios.post(`/api/investigations/${rootInvestigationId}/notes`, { body });
      setCaseNotes((prev) => [res.data, ...prev]);
      setNewCaseNoteBody('');
      setCaseNotesSuccess('Note added.');
    } catch (e) {
      setCaseNotesError('Failed to add note: ' + (e.response?.data?.message || e.message));
    } finally {
      setPostingCaseNote(false);
    }
  };

  const handleSaveCaseNote = async (noteInvestigationId, noteId) => {
    try {
      setSavingCaseNoteId(noteId);
      setCaseNotesError(null);
      setCaseNotesSuccess(null);
      const res = await axios.patch(`/api/investigations/${noteInvestigationId}/notes/${noteId}`, {
        body: editCaseNoteDraft,
      });
      mergeCaseNoteInList(res.data);
      setEditingCaseNoteId(null);
      setEditCaseNoteDraft('');
      setCaseNotesSuccess('Note updated.');
    } catch (e) {
      const msg = e.response?.status === 403 ? 'You can only edit your own notes.' : (e.response?.data?.message || e.message);
      setCaseNotesError('Failed to save note: ' + msg);
    } finally {
      setSavingCaseNoteId(null);
    }
  };

  const handleToggleCaseNoteReaction = async (noteInvestigationId, noteId, emoji) => {
    if (!user?.email) return;
    try {
      setCaseNoteReactionBusyId(`${noteId}-${emoji}`);
      setCaseNotesError(null);
      const res = await axios.post(`/api/investigations/${noteInvestigationId}/notes/${noteId}/reactions`, { emoji });
      mergeCaseNoteInList(res.data);
    } catch (e) {
      setCaseNotesError('Failed to update reaction: ' + (e.response?.data?.message || e.message));
    } finally {
      setCaseNoteReactionBusyId(null);
    }
  };

  const handleDeleteCaseNote = async (noteInvestigationId, noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      setDeletingCaseNoteId(noteId);
      setCaseNotesError(null);
      setCaseNotesSuccess(null);
      await axios.delete(`/api/investigations/${noteInvestigationId}/notes/${noteId}`);
      setCaseNotes((prev) => prev.filter((n) => n.id !== noteId));
      setCaseNotesSuccess('Note deleted.');
    } catch (e) {
      const msg = e.response?.status === 403 ? 'You can only delete your own notes.' : (e.response?.data?.message || e.message);
      setCaseNotesError('Failed to delete note: ' + msg);
    } finally {
      setDeletingCaseNoteId(null);
    }
  };

  if (loading) {
    return (
      <PdsBox justifyContent="center" padding="xl" direction="column" alignItems="center" gap="md">
        <PdsLoader size="lg" />
        {awaitingCaseIngest && (
          <PdsMantineText type="body" color="neutral" style={{ textAlign: 'center', maxWidth: 440 }}>
            Opening your new case… If you just saved from Investigate, findings are being stored on the server and may take a few minutes to appear.
          </PdsMantineText>
        )}
      </PdsBox>
    );
  }
  if (error) {
    return <PdsNotification color="warning" dismissible={false}>{error}</PdsNotification>;
  }
  if (!caseData) return null;

  return (
    <>
    <PdsBox direction="column" gap="md" style={{ position: 'relative' }}>
      {/* Case page: primary tabs + Back */}
      <PdsBox direction="row" justifyContent="space-between" alignItems="center" wrap="wrap" gap="md">
        <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center" style={{ borderBottom: '2px solid #e1e4e8', flex: '1 1 280px' }}>
          <PdsTab
            value="details"
            selected={caseSubPage === 'details'}
            color="action"
            onClick={() => setCaseSubPage('details')}
            leadingIcon={<PdsTicketIcon />}
          >
            Case Details
          </PdsTab>
          <PdsTab
            value="notes"
            selected={caseSubPage === 'notes'}
            color="action"
            onClick={() => setCaseSubPage('notes')}
            leadingIcon={<PdsDocument1Icon />}
          >
            Comments
          </PdsTab>
        </PdsBox>
        <PdsMantineButton
          component={Link}
          to="/cases"
          rank="outline"
          color="neutral"
          leadingIcon={<PdsArrowLeftIcon />}
        >
          Back
        </PdsMantineButton>
      </PdsBox>

      {caseSubPage === 'details' && (
      <>
      {/* Case info */}
      <PdsCard border borderRadius="md" padding="md" direction="column" gap="md">
        {/* Title row */}
        <PdsBox direction="row" justifyContent="space-between" alignItems="flex-start" gap="md" wrap="wrap">
          <PdsBox direction="column" gap="xs" style={{ flex: '1 1 200px', minWidth: 0 }}>
            {caseData.title && (
              <PdsMantineText type="headline" fontWeight="strong" className="argus-type-section-heading">
                {caseData.title}
              </PdsMantineText>
            )}
            <PdsBox direction="row" alignItems="center" gap="sm" wrap="wrap">
              <PdsMantineText type="textline" className="argus-data argus-data-focal">
                {caseData.target}
              </PdsMantineText>
              <PdsTag color={STATUS_COLOR[caseData.status] || 'neutral'} dismissible={false} size="sm">
                {caseData.status}
              </PdsTag>
            </PdsBox>
          </PdsBox>

          {/* Action buttons */}
          <PdsBox direction="column" justifyContent="space-between" style={{ alignSelf: 'stretch', flexShrink: 0 }}>
            <PdsBox direction="row" gap="xs" wrap="wrap">
              <PdsMantineButton rank="outline" color="neutral" size="sm" onClick={() => setShowEditCaseModal(true)} leadingIcon={<PdsEditIcon />}>
                Edit
              </PdsMantineButton>
              {caseData.status === 'ACTIVE' && (
                <PdsMantineButton rank="outline" color="warning" size="sm" onClick={() => handleStatusChange('PAUSED')} leadingIcon={<PdsStopIcon />}>
                  Pause
                </PdsMantineButton>
              )}
              {caseData.status === 'PAUSED' && (
                <PdsMantineButton rank="outline" color="warning" size="sm" onClick={() => handleStatusChange('ACTIVE')} leadingIcon={<PdsStopIcon />}>
                  Unpause
                </PdsMantineButton>
              )}
              {(caseData.status === 'ACTIVE' || caseData.status === 'PAUSED') && (
                <PdsMantineButton rank="outline" color="action" size="sm" onClick={() => handleStatusChange('COMPLETED')} leadingIcon={<PdsCheckConfirmIcon />}>
                  Complete
                </PdsMantineButton>
              )}
              {caseData.status === 'COMPLETED' && (
                <>
                  <PdsMantineButton rank="outline" color="warning" size="sm" onClick={() => handleStatusChange('PAUSED')} leadingIcon={<PdsStopIcon />}>
                    Pause
                  </PdsMantineButton>
                  <PdsMantineButton rank="outline" color="action" size="sm" onClick={() => handleStatusChange('ACTIVE')} leadingIcon={<PdsCheckConfirmIcon />}>
                    Reopen
                  </PdsMantineButton>
                </>
              )}
              <PdsMantineButton rank="outline" color="attention" size="sm" onClick={handleDeleteCase} leadingIcon={<PdsTrashDeleteIcon />}>
                Delete
              </PdsMantineButton>
            </PdsBox>
            <PdsBox direction="row" justifyContent="end">
              <PdsMantineButton
                rank="outline"
                color="action"
                size="sm"
                onClick={handleScanLinkage}
                disabled={scanLinkageBusy}
                leadingIcon={scanLinkageBusy ? <PdsSpinnerIcon /> : <PdsLinkIcon />}
              >
                {scanLinkageBusy ? 'Scanning linkages…' : 'Scan Linkage'}
              </PdsMantineButton>
            </PdsBox>
          </PdsBox>
        </PdsBox>

        {caseData.description && (
          <PdsBox className="case-description-panel">
            <PdsMantineText type="body" color="neutral" component="p" style={{ margin: 0 }}>
              {caseData.description}
            </PdsMantineText>
          </PdsBox>
        )}

        {/* Metadata row */}
        <PdsBox direction="row" gap="lg" wrap="wrap" alignItems="center">
          <PdsBox direction="row" alignItems="center" gap="xs">
            <PdsMantineText type="textline" color="neutral" fontWeight="strong" component="span" className="argus-type-label">
              Created:
            </PdsMantineText>
            <PdsMantineText type="textline" component="span">{new Date(caseData.createdAt).toLocaleString()}</PdsMantineText>
          </PdsBox>
          <PdsBox direction="row" alignItems="center" gap="xs">
            <PdsMantineText type="textline" color="neutral" fontWeight="strong" component="span" className="argus-type-label">
              By:
            </PdsMantineText>
            <PdsMantineText type="textline" component="span">
              {caseData.createdBy ? displayUsername(caseData.createdBy) : 'Unknown'}
            </PdsMantineText>
          </PdsBox>
          {caseData.completedAt && (
            <PdsBox direction="row" alignItems="center" gap="xs">
              <PdsMantineText type="textline" color="neutral" fontWeight="strong" component="span" className="argus-type-label">
                Completed:
              </PdsMantineText>
              <PdsMantineText type="textline" component="span">{new Date(caseData.completedAt).toLocaleString()}</PdsMantineText>
            </PdsBox>
          )}
          <PdsBox direction="row" alignItems="center" gap="xs">
            <PdsTicketIcon color="neutral" size="sm" />
            {caseData.jiraId ? (
              <PdsLink
                href={`https://sportradar.atlassian.net/browse/${caseData.jiraId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {caseData.jiraId} <PdsExternalLinkIcon size="xs" />
              </PdsLink>
            ) : (
              <PdsMantineText type="textline" color="neutral" component="span" style={{ fontStyle: 'italic' }}>No JIRA ticket</PdsMantineText>
            )}
          </PdsBox>
        </PdsBox>

        {actionError && (
          <PdsNotification color="warning" onDismiss={() => setActionError(null)}>
            {actionError}
          </PdsNotification>
        )}
        {linkageScanMessage && (
          <PdsNotification color="success" onDismiss={() => setLinkageScanMessage(null)} size="sm">
            {linkageScanMessage}
          </PdsNotification>
        )}
        {linkageScanError && (
          <PdsNotification color="warning" onDismiss={() => setLinkageScanError(null)}>
            {linkageScanError}
          </PdsNotification>
        )}
      </PdsCard>

      {/* Network diagram */}
      {investigations.length > 0 && (
        <InvestigationNetworkFlow
          caseId={caseId}
          investigations={investigations}
          onNodeClick={setSelectedInvestigationId}
          onFindingsBadgeClick={(invId) => {
            setSelectedInvestigationId(invId);
            setFindingsTabNavigateNonce((n) => n + 1);
          }}
          onDeleteInvestigation={handleDeleteInvestigation}
          selectedInvestigationId={selectedInvestigationId}
          fitViewNodeId={homeNavFitViewNodeId}
          onFitViewToTargetConsumed={onFitViewToTargetConsumed}
          linkageRefreshNonce={linkageRefreshNonce}
        />
      )}

      {/* Investigation data */}
      {investigations.length === 0 ? (
        <PdsCard border borderRadius="md" padding="md">
          <PdsMantineText type="body" color="neutral">No nodes yet.</PdsMantineText>
        </PdsCard>
      ) : (
        selectedInvestigationId && (
          <InvestigationData
            key={selectedInvestigationId}
            investigationId={selectedInvestigationId}
            investigationStatus={selectedInvestigation?.status}
            caseId={caseId}
            findingsTabNavigateNonce={findingsTabNavigateNonce}
            caseInvestigations={investigations}
            onInvestigationsUpdated={async (newSelectedId) => {
              try {
                const invs = await axios.get(`/api/investigations/case/${caseId}`);
                const data = Array.isArray(invs.data) ? invs.data : [];
                setInvestigations(data);
                if (newSelectedId) setSelectedInvestigationId(newSelectedId);
              } catch (e) {
                setActionError('Failed to refresh nodes: ' + (e.response?.data?.message || e.message));
              }
            }}
            onInvestigationWorkflowToolsSelected={setPendingMaskWorkflowTools}
            onRerunWorkflowPosted={markInvestigationRunningLocally}
            caseWorkflowMaskActive={workflowMaskVisible}
            workflowDetailsSnapshot={workflowDetailsSnapshotForSelected}
            jumpToFindingId={
              homeJump?.findingId
              && (!homeJump.investigationId || homeJump.investigationId === selectedInvestigationId)
                ? homeJump.findingId
                : null
            }
            jumpToSourceKind={
              homeJump?.sourceKind
              && (!homeJump.investigationId || homeJump.investigationId === selectedInvestigationId)
                ? homeJump.sourceKind
                : null
            }
            jumpToNeedle={
              homeJump?.needle
              && (!homeJump.investigationId || homeJump.investigationId === selectedInvestigationId)
                ? homeJump.needle
                : null
            }
          />
        )
      )}
      </>
      )}

      {caseSubPage === 'notes' && (
        <PdsBox direction="column" gap="md">
          <PdsMantineText type="headline" fontWeight="strong" className="argus-type-section-heading">
            Comments
          </PdsMantineText>
          <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
            Comments are stored against the nodes for this case; This list shows all comments, with the newest first. You can only edit or delete your own comments.
          </PdsMantineText>
          {caseNotesSuccess && (
            <PdsNotification color="success" onDismiss={() => setCaseNotesSuccess(null)} size="sm">
              {caseNotesSuccess}
            </PdsNotification>
          )}
          {caseNotesLoading && (
            <PdsBox direction="row" alignItems="center" gap="sm" role="status" aria-live="polite" aria-label="Loading notes">
              <PdsLoader size="sm" />
              <PdsMantineText type="textline" color="neutral" component="span">Loading notes…</PdsMantineText>
            </PdsBox>
          )}
          {caseNotesError && (
            <PdsNotification color="warning" onDismiss={() => setCaseNotesError(null)}>
              {caseNotesError}
            </PdsNotification>
          )}
          <PdsBox direction="column" gap="sm">
            {caseNotes.map((note) => {
              const noteInvId = note.investigationId;
              const canEdit =
                user?.email &&
                note.authorUserId === user.email &&
                note.authorUserId !== LEGACY_NOTES_AUTHOR_ID;
              const isEditing = editingCaseNoteId === note.id;
              return (
                <PdsCard key={note.id} border borderRadius="md" padding="md" data-note-id={note.id}>
                  {isEditing ? (
                    <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
                      <PdsMantineText type="textline" fontWeight="strong" component="span">
                        Edit note
                      </PdsMantineText>
                      <PdsTextArea
                        value={editCaseNoteDraft}
                        onChange={(e) => setEditCaseNoteDraft(e.target.value)}
                        stretch
                        rows={6}
                        disabled={!!savingCaseNoteId}
                      />
                      <PdsBox direction="row" gap="sm">
                        <PdsMantineButton
                          rank="fill"
                          color="action"
                          size="sm"
                          onClick={() => noteInvId && handleSaveCaseNote(noteInvId, note.id)}
                          disabled={!!savingCaseNoteId || !noteInvId}
                          leadingIcon={savingCaseNoteId === note.id ? <PdsSpinnerIcon /> : <PdsCheckConfirmIcon />}
                        >
                          {savingCaseNoteId === note.id ? 'Saving…' : 'Save'}
                        </PdsMantineButton>
                        <PdsMantineButton
                          rank="outline"
                          color="neutral"
                          size="sm"
                          onClick={() => {
                            setEditingCaseNoteId(null);
                            setEditCaseNoteDraft('');
                          }}
                          disabled={!!savingCaseNoteId}
                        >
                          Cancel
                        </PdsMantineButton>
                      </PdsBox>
                    </PdsBox>
                  ) : (
                    <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
                      <div className="cases-search-result-card investigation-note-card">
                        <PdsBox className="cases-search-result-details-row" style={{ minWidth: 0 }}>
                          <span className="cases-search-expand-cell-spacer" aria-hidden />
                          <PdsBox direction="column" gap="xs" style={{ minWidth: 0, flex: 1 }}>
                            <PdsMantineText type="textline" fontWeight="strong" component="span">
                              {formatNoteAuthorLine(note.authorUserId)}
                            </PdsMantineText>
                            {note.investigationTarget ? (
                              <PdsMantineText type="textline" color="neutral" component="span">
                                Node: {note.investigationTarget}
                              </PdsMantineText>
                            ) : null}
                            <PdsMantineText type="body" component="span" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {note.body || ''}
                            </PdsMantineText>
                          </PdsBox>
                        </PdsBox>
                        <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                          <PdsMantineText type="textline" color="neutral" component="span">
                            Created
                          </PdsMantineText>
                          <PdsMantineText type="textline" component="span">
                            {formatNoteListDate(note.createdAt)}
                          </PdsMantineText>
                        </PdsBox>
                        <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                          <PdsMantineText type="textline" color="neutral" component="span">
                            Last updated
                          </PdsMantineText>
                          <PdsMantineText type="textline" component="span">
                            {note.updatedAt ? formatNoteListDate(note.updatedAt) : '—'}
                          </PdsMantineText>
                        </PdsBox>
                        <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                          <PdsMantineText type="textline" color="neutral" component="span">Type</PdsMantineText>
                          <PdsTag color="neutral" dismissible={false} size="sm">
                            NOTE
                          </PdsTag>
                        </PdsBox>
                        <PdsBox direction="row" gap="xs" alignItems="center" justifyContent="flex-end" wrap="nowrap" className="cases-search-result-actions">
                          {canEdit && (
                            <>
                              <PdsMantineButton
                                rank="ghost"
                                color="neutral"
                                size="sm"
                                leadingIcon={<PdsEditIcon />}
                                onClick={() => {
                                  setEditingCaseNoteId(note.id);
                                  setEditCaseNoteDraft(note.body || '');
                                }}
                                disabled={deletingCaseNoteId === note.id}
                              >
                                Edit
                              </PdsMantineButton>
                              <PdsMantineButton
                                rank="ghost"
                                color="neutral"
                                size="sm"
                                leadingIcon={deletingCaseNoteId === note.id ? <PdsSpinnerIcon /> : <PdsTrashDeleteIcon />}
                                onClick={() => noteInvId && handleDeleteCaseNote(noteInvId, note.id)}
                                disabled={deletingCaseNoteId === note.id || !noteInvId}
                              >
                                {deletingCaseNoteId === note.id ? 'Deleting…' : 'Delete'}
                              </PdsMantineButton>
                            </>
                          )}
                        </PdsBox>
                      </div>
                      <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexWrap: 'wrap' }}>
                        {NOTE_REACTION_EMOJIS.map((emoji) => {
                          const reactors = (note.reactions && note.reactions[emoji]) || [];
                          const count = Array.isArray(reactors) ? reactors.length : 0;
                          const self = user?.email && reactors.includes(user.email);
                          const busy = caseNoteReactionBusyId === `${note.id}-${emoji}`;
                          const canToggle = !!user?.email;
                          return (
                            <button
                              key={emoji}
                              type="button"
                              className="argus-type-ui-interactive"
                              onClick={() => noteInvId && handleToggleCaseNoteReaction(noteInvId, note.id, emoji)}
                              disabled={busy || !user?.email || !noteInvId}
                              title={formatNoteReactionTooltip(reactors, canToggle)}
                              style={{
                                border: '1px solid #d0d7de',
                                borderRadius: 999,
                                padding: '2px 10px',
                                background: self ? '#eef2ff' : '#fff',
                                cursor: user?.email ? 'pointer' : 'not-allowed',
                                lineHeight: 1.4,
                              }}
                            >
                              {emoji}
                              {count > 0 ? ` ${count}` : ''}
                            </button>
                          );
                        })}
                      </PdsBox>
                    </PdsBox>
                  )}
                </PdsCard>
              );
            })}
          </PdsBox>
          <PdsCard border borderRadius="md" padding="md">
            <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
              <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-card-title">
                Add comments
              </PdsMantineText>
              {!rootInvestigationId && (
                <PdsMantineText type="textline" color="neutral" component="p" style={{ margin: 0 }}>
                  Posting requires at least one node on this case (the root node). Run or add a node from Case Details first.
                </PdsMantineText>
              )}
              <PdsTextArea
                aria-label="Note text"
                placeholder="Observations, hypotheses, next steps…"
                value={newCaseNoteBody}
                onChange={(e) => {
                  setNewCaseNoteBody(e.target.value);
                  if (caseNotesError) setCaseNotesError(null);
                }}
                stretch
                rows={5}
                disabled={postingCaseNote}
              />
              <PdsMantineButton
                rank="fill"
                color="action"
                size="sm"
                onClick={handlePostCaseNote}
                disabled={postingCaseNote || !newCaseNoteBody.trim() || !rootInvestigationId}
                leadingIcon={postingCaseNote ? <PdsSpinnerIcon /> : <PdsPlusAddIcon />}
              >
                {postingCaseNote ? 'Posting…' : 'Post note'}
              </PdsMantineButton>
            </PdsBox>
          </PdsCard>
        </PdsBox>
      )}


      {/* Scroll to top */}
      {showScrollTop && (
        <PdsMantineIconButton
          rank="fill"
          color="action"
          size="lg"
          icon={<PdsChevronUpSmIcon />}
          onClick={scrollToTop}
          aria-label="Scroll to top"
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            borderRadius: '50%',
            zIndex: 1000,
          }}
        />
      )}

      {/* Edit Case Modal */}
      {showEditCaseModal && (
        <CaseEditModal
          caseData={caseData}
          onClose={() => setShowEditCaseModal(false)}
          onSuccess={(updatedCase) => {
            setCaseData(updatedCase);
            setShowEditCaseModal(false);
          }}
        />
      )}
    </PdsBox>

    {workflowMaskVisible && (
      <CaseWorkflowMaskOverlay
        awaitingCaseIngest={awaitingCaseIngest}
        investigationsEmpty={investigations.length === 0}
        targetValue={maskDetailsSnapshot?.targetValue ?? maskHudTargetInvestigation?.targetValue}
        detailSnapshot={maskDetailsSnapshot}
        workflowToolsOverride={pendingMaskWorkflowTools}
        onDismiss={() => setWorkflowMaskDismissed(true)}
      />
    )}
    </>
  );
};

// ─── InvestigationData ────────────────────────────────────────────────────────

/** Findings whose value is a domain/URL target: show Investigate (tools) + Add to map; others (e.g. Operating Company) are add-only. */
const FINDING_TYPES_FULL_INVESTIGATE = ['LINKS', 'NETWORK_TRAFFIC', 'RELATED_DOMAIN', 'SOURCE_CODES', 'CUSTOM'];

/** Tool name in the “search across all results” hint (must match activeSource values). */
const KEYWORD_FILTER_TAB_LABEL = {
  securitytrails: 'SecurityTrails',
  'securitytrails-historical': 'SecurityTrails (Historical)',
  urlscan: 'URLScan',
  publicwww: 'PublicWWW',
  crtsh: 'CRT.sh',
  manual: 'Findings',
};

/** AND/OR pill between keyword chips in investigation result search bar. */
function KeywordJoinToggle({ value, onChange }) {
  return (
    <span
      role="group"
      aria-label="Combine with previous term"
      style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #d0d7de', flexShrink: 0 }}
    >
      {['AND', 'OR'].map((op) => (
        <button
          key={op}
          type="button"
          onClick={() => onChange(op)}
          style={{
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.02em',
            border: 'none',
            cursor: 'pointer',
            background: value === op ? '#0969da' : '#f6f8fa',
            color: value === op ? '#fff' : '#24292f',
          }}
        >
          {op}
        </button>
      ))}
    </span>
  );
}

/** Findings tab empty state (case page only; tab hidden in home domain preview). */
const FINDINGS_TAB_EMPTY_EXPLAINER = (
  <>
    <PdsMantineText type="body" color="neutral" component="p" style={{ marginTop: 0, lineHeight: 1.55 }}>
      Use <strong>Add Finding</strong> (above this tab strip) to record structured observations that automated tools do not capture on their own. You can store items such as:
    </PdsMantineText>
    <ul style={{ margin: '8px 0 0', paddingLeft: 22, color: '#57606a', lineHeight: 1.55 }}>
      <li>Operating company or contact details</li>
      <li>Related domains, typosquats, or links worth tracking</li>
      <li>Network traffic endpoints or source-code references</li>
      <li>Custom-labelled findings when none of the presets fit</li>
    </ul>
    <PdsMantineText type="textline" color="neutral" component="p" style={{ marginBottom: 0 }}>
      Entries listed here can spawn follow-up nodes from the network map where Argus supports it (for example domains and URLs).
    </PdsMantineText>
  </>
);

/** Findings tab intro when this investigation already has manual findings. */
const FINDINGS_TAB_LIST_EXPLAINER = (
  <PdsBox direction="column" gap="xs" style={{ maxWidth: 960 }}>
    <PdsMantineText type="body" color="neutral" component="p" style={{ margin: 0, lineHeight: 1.55 }}>
      Findings are manual observations for this node, grouped by type below. Use <strong>Add Finding</strong> (above the tab strip) to add more.
    </PdsMantineText>
    <PdsMantineText type="textline" color="neutral" component="p" style={{ margin: 0, lineHeight: 1.55 }}>
      For domains and URLs you can run tools or add a node to the network map from each row by clicking on{' '}
      <span
        aria-hidden
        style={{ display: 'inline-flex', verticalAlign: 'text-bottom', margin: '0 2px', pointerEvents: 'none' }}
      >
        <PdsMantineIconButton
          size="sm"
          rank="fill"
          color="action"
          icon={<PdsSearchIcon />}
          tabIndex={-1}
          onClick={() => {}}
          aria-label="Run tools for this target"
          style={{ cursor: 'default' }}
        />
      </span>
      {' '}or the{' '}
      <span
        aria-hidden
        style={{ display: 'inline-flex', verticalAlign: 'text-bottom', margin: '0 2px', pointerEvents: 'none' }}
      >
        <PdsMantineIconButton
          size="sm"
          rank="outline"
          color="neutral"
          icon={<PdsPlusAddIcon />}
          tabIndex={-1}
          onClick={() => {}}
          aria-label="Add node to map without running tools"
          style={{ cursor: 'default' }}
        />
      </span>
      {' '}button.
    </PdsMantineText>
    <PdsMantineText type="textline" color="neutral" component="p" style={{ margin: 0, lineHeight: 1.55 }}>
      Edit or delete findings you added from this list.
    </PdsMantineText>
  </PdsBox>
);

export const InvestigationData = ({
  investigationId,
  /** Polled from case list; when this transitions PENDING/RUNNING → COMPLETED/FAILED, details are refetched so tabs are not stale. */
  investigationStatus,
  preloadedData,
  /** When true (home domain preview still polling), empty states and banners explain streaming instead of "no data". */
  streamingPreview = false,
  caseId,
  onInvestigationsUpdated,
  /** Home preview: pivot search to another domain / IP / keyword (wired from Dashboard). */
  onPreviewContinue,
  /** Home preview: merge refreshed investigation details into Dashboard preview state. */
  onPreviewDataUpdated,
  caseInvestigations = [],
  /** Case page: after chart findings-badge click, switch source tabs to Findings. */
  findingsTabNavigateNonce = 0,
  /** Case page: after a successful Investigate trigger, pass the tool flags for the workflow HUD until server persists them. */
  onInvestigationWorkflowToolsSelected,
  /** Case page: rerun POST succeeded — parent may show workflow mask until RUNNING appears on polled investigations. */
  onRerunWorkflowPosted,
  /** Case page: full-screen workflow mask is up — soft-refresh details on an interval and hide redundant Refresh. */
  caseWorkflowMaskActive = false,
  /** Case page: polled /details for the selected node while workflow mask is up (parent HUD poll). */
  workflowDetailsSnapshot = null,
  /** Case page: finding id to scroll to on mount (from home search panel navigation). */
  jumpToFindingId = null,
  /** Case page: tool table that matched on home search — open that tab/section. */
  jumpToSourceKind = null,
  jumpToNeedle = null,
}) => {
  const isPreviewMode = !!preloadedData;
  const [details, setDetails] = useState(preloadedData || null);
  const [loading, setLoading] = useState(!preloadedData);
  /** Soft reload (Refresh / post-workflow) keeps tabs visible instead of replacing the whole panel with a spinner. */
  const [detailsRefreshing, setDetailsRefreshing] = useState(false);

  // Home preview polls and merges partial API snapshots into `preloadedData`. Without this, `details`
  // would stay stuck on the first snapshot (e.g. SecurityTrails only) and never show crt.sh / UrlScan.
  useEffect(() => {
    if (preloadedData) {
      setDetails(preloadedData);
    }
  }, [preloadedData]);
  const [error, setError] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [crtShRerunning, setCrtShRerunning] = useState(false);
  const [publicWwwRunning, setPublicWwwRunning] = useState(false);
  const [publicWwwRefreshNonce, setPublicWwwRefreshNonce] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [showAddFindingModal, setShowAddFindingModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [otherInformation, setOtherInformation] = useState([]);
  const [showInvestigationModal, setShowInvestigationModal] = useState(false);
  const [targetDomainForInvestigation, setTargetDomainForInvestigation] = useState('');
  const [isRerunMode, setIsRerunMode] = useState(false);
  const [deriveFromValue, setDeriveFromValue] = useState(null);
  /** When set to 'urlscan', child trigger stores URL_SCAN / URL_SEARCH for graph edge labels instead of row informationType. */
  const [investigateLaunchContext, setInvestigateLaunchContext] = useState(null);
  /** Persisted chart edge label for POST placeholder/trigger while the investigation tools modal runs. */
  const [investigationModalConnectionLabel, setInvestigationModalConnectionLabel] = useState(null);
  const [showEditFindingModal, setShowEditFindingModal] = useState(false);
  const [editingFinding, setEditingFinding] = useState(null);
  const [linkages, setLinkages] = useState([]);
  const [caseGraphLinks, setCaseGraphLinks] = useState([]);
  const [linkageModal, setLinkageModal] = useState({ open: false, data: null });
  const [keywordFilterQuery, setKeywordFilterQuery] = useState(() => emptyKeywordQuery());
  const [keywordFilterDraft, setKeywordFilterDraft] = useState('');

  const sortedFindings = useMemo(() => {
    if (!otherInformation?.length) return [];
    return [...otherInformation].sort((a, b) => {
      const ta = a.informationType || '';
      const tb = b.informationType || '';
      if (ta !== tb) return ta.localeCompare(tb);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [otherInformation]);

  /** Condensed SecurityTrails sections only; other tabs always use comfortable layout (no density toggle). */
  const [securityTrailsViewDensity, setSecurityTrailsViewDensity] = useState('compact');
  /** In condensed mode, which section cards are expanded (multiple allowed). All sections start collapsed. */
  const [securityTrailsOpenSectionIds, setSecurityTrailsOpenSectionIds] = useState(() => new Set());

  const toggleSecurityTrailsSection = (sectionId) => {
    setSecurityTrailsOpenSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  useEffect(() => {
    if (jumpToSourceKind && jumpToSourceKind !== 'OTHER_INFORMATION') {
      const dest = destinationForToolSourceKind(jumpToSourceKind, { needle: jumpToNeedle });
      setSecurityTrailsOpenSectionIds(
        dest.tab === 'securitytrails' && dest.sectionId ? new Set([dest.sectionId]) : new Set(),
      );
      return;
    }
    setSecurityTrailsOpenSectionIds(new Set());
  }, [securityTrailsViewDensity, investigationId, jumpToSourceKind, jumpToNeedle]);

  const [stSectionFilters, setStSectionFilters] = useState({
    historicalWhois: '',
    nameservers: '',
    aRecords: '',
    aaaaRecords: '',
    soaRecords: '',
    txtRecords: '',
    mxRecords: '',
    subdomains: '',
    domainsOnIp: '',
    domainsOnNs: '',
  });

  const [crtFilter, setCrtFilter] = useState('ALL');
  const [findingTypeFilter, setFindingTypeFilter] = useState('ALL');
  const [domainsOnIpSiblingFilter, setDomainsOnIpSiblingFilter] = useState('ALL');
  const [domainsOnNsNameserverFilter, setDomainsOnNsNameserverFilter] = useState('ALL');
  /** Cert IDs whose SANs list is expanded; "Expand all" fills this with every displayed cert that has SANs. */
  const [expandedSanCertIds, setExpandedSanCertIds] = useState(() => new Set());

  const toggleCertSans = (certId) => {
    setExpandedSanCertIds((prev) => {
      const next = new Set(prev);
      if (next.has(certId)) {
        next.delete(certId);
      } else {
        next.add(certId);
      }
      return next;
    });
  };

  const certificateStatusCounts = useMemo(() => {
    const certs = details?.certificates || [];
    let expired = 0;
    let expiring = 0;
    let valid = 0;
    const horizon = Date.now() + 30 * 24 * 60 * 60 * 1000;
    for (const cert of certs) {
      const isExpired = cert.notAfter && new Date(cert.notAfter) < new Date();
      const expiringSoon = !isExpired && cert.notAfter && new Date(cert.notAfter) < new Date(horizon);
      if (isExpired) expired += 1;
      else if (expiringSoon) expiring += 1;
      else valid += 1;
    }
    return { all: certs.length, expired, expiring, valid };
  }, [details?.certificates]);

  const filteredCertificates = useMemo(() => {
    if (!details?.certificates?.length) return [];
    const filtered = details.certificates.filter((cert) => {
      const expired = cert.notAfter && new Date(cert.notAfter) < new Date();
      const expiringSoon = !expired && cert.notAfter && new Date(cert.notAfter) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      if (crtFilter === 'EXPIRED') return expired;
      if (crtFilter === 'EXPIRING') return expiringSoon;
      if (crtFilter === 'VALID') return !expired && !expiringSoon;
      return true;
    });
    return sortCertificatesByFirstSeen(filtered);
  }, [details?.certificates, crtFilter]);

  const findingTypes = useMemo(
    () => [...new Set(sortedFindings.map((f) => f.informationType).filter(Boolean))].sort(),
    [sortedFindings],
  );

  const filteredFindings = useMemo(() => {
    if (!sortedFindings?.length) return [];
    if (findingTypeFilter === 'ALL') return sortedFindings;
    return sortedFindings.filter((f) => f.informationType === findingTypeFilter);
  }, [sortedFindings, findingTypeFilter]);

  const kwActive = isKeywordQueryActive(keywordFilterQuery);

  const filteredWhoisVisible = useMemo(() => {
    if (!details?.whoisRecord) return false;
    if (!kwActive) return true;
    return objectMatchesKeywordFilters(details.whoisRecord, keywordFilterQuery);
  }, [details?.whoisRecord, keywordFilterQuery, kwActive]);

  const filteredHistoricalWhoisList = useMemo(() => {
    const list = details?.historicalWhois || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.historicalWhois, keywordFilterQuery, kwActive]);

  const filteredHistoricalDnsRecordsList = useMemo(() => {
    const list = details?.historicalDnsRecords || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.historicalDnsRecords, keywordFilterQuery, kwActive]);

  const filteredNameserversList = useMemo(() => {
    const list = details?.nameservers || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.nameservers, keywordFilterQuery, kwActive]);

  const filteredIpAddressesList = useMemo(() => {
    const list = details?.ipAddresses || [];
    if (!kwActive) return list;
    return list.filter((ip) => {
      const geo = details?.ipGeolocations?.find((g) => g.ipAddress === ip.ipAddress);
      const hay = [ip.ipAddress, ip.domain, geo?.city, geo?.country, geo?.asn, geo?.asnOrg, geo?.asn_org, geo?.postalCode].filter(Boolean).join(' ');
      return textMatchesKeywordFilters(hay, keywordFilterQuery);
    });
  }, [details?.ipAddresses, details?.ipGeolocations, keywordFilterQuery, kwActive]);

  const filteredMxRecordsList = useMemo(() => {
    const list = details?.mxRecords || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.mxRecords, keywordFilterQuery, kwActive]);

  const filteredSubdomainsList = useMemo(() => {
    const list = details?.subdomains || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.subdomains, keywordFilterQuery, kwActive]);

  const filteredDomainsOnIpList = useMemo(() => {
    const list = details?.domainsOnIp || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.domainsOnIp, keywordFilterQuery, kwActive]);

  const filteredDomainsOnNsList = useMemo(() => {
    const list = details?.domainsOnNs || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.domainsOnNs, keywordFilterQuery, kwActive]);

  const filteredARecordsList = useMemo(
    () => filterARecords(filteredIpAddressesList),
    [filteredIpAddressesList],
  );

  const filteredAaaaRecordsList = useMemo(
    () => filterAaaaRecords(filteredIpAddressesList),
    [filteredIpAddressesList],
  );

  const filteredSoaRecordsList = useMemo(() => {
    const list = details?.soaRecords || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.soaRecords, keywordFilterQuery, kwActive]);

  const filteredTxtRecordsList = useMemo(() => {
    const list = details?.txtRecords || [];
    if (!kwActive) return list;
    return list.filter((row) => objectMatchesKeywordFilters(row, keywordFilterQuery));
  }, [details?.txtRecords, keywordFilterQuery, kwActive]);

  const historicalWhoisDisplayList = useMemo(
    () =>
      filteredHistoricalWhoisList.filter((row) =>
        securityTrailsCardTextMatch(
          stSectionFilters.historicalWhois,
          [row.registrar, row.regName, row.regOrg, row.regEmail, row.modDate].filter(Boolean).join(' '),
        ),
      ),
    [filteredHistoricalWhoisList, stSectionFilters.historicalWhois],
  );

  const nameserversDisplayList = useMemo(
    () =>
      filteredNameserversList.filter((row) =>
        securityTrailsCardTextMatch(stSectionFilters.nameservers, `${row.nameserver} ${row.domain}`),
      ),
    [filteredNameserversList, stSectionFilters.nameservers],
  );

  const aRecordsDisplayList = useMemo(
    () =>
      filteredARecordsList.filter((ip) => {
        const geo = details?.ipGeolocations?.find((g) => g.ipAddress === ip.ipAddress);
        const blob = [ip.ipAddress, ip.domain, geo?.city, geo?.country, geo?.asn, geo?.postalCode].filter(Boolean).join(' ');
        return securityTrailsCardTextMatch(stSectionFilters.aRecords, blob);
      }),
    [filteredARecordsList, stSectionFilters.aRecords, details?.ipGeolocations],
  );

  const aaaaRecordsDisplayList = useMemo(
    () =>
      filteredAaaaRecordsList.filter((ip) => {
        const geo = details?.ipGeolocations?.find((g) => g.ipAddress === ip.ipAddress);
        const blob = [ip.ipAddress, ip.domain, geo?.city, geo?.country, geo?.asn, geo?.postalCode].filter(Boolean).join(' ');
        return securityTrailsCardTextMatch(stSectionFilters.aaaaRecords, blob);
      }),
    [filteredAaaaRecordsList, stSectionFilters.aaaaRecords, details?.ipGeolocations],
  );

  const soaRecordsDisplayList = useMemo(
    () =>
      filteredSoaRecordsList.filter((row) =>
        securityTrailsCardTextMatch(
          stSectionFilters.soaRecords,
          [row.mname, row.rname, row.serial, row.refresh, row.retry, row.expire, row.minimum, row.domain]
            .filter((v) => v != null && v !== '')
            .join(' '),
        ),
      ),
    [filteredSoaRecordsList, stSectionFilters.soaRecords],
  );

  const txtRecordsDisplayList = useMemo(
    () =>
      filteredTxtRecordsList.filter((row) =>
        securityTrailsCardTextMatch(stSectionFilters.txtRecords, `${row.txtValue || ''} ${row.domain || ''}`),
      ),
    [filteredTxtRecordsList, stSectionFilters.txtRecords],
  );

  const mxRecordsDisplayList = useMemo(
    () =>
      filteredMxRecordsList.filter((row) =>
        securityTrailsCardTextMatch(stSectionFilters.mxRecords, `${row.priority} ${row.mxHost}`),
      ),
    [filteredMxRecordsList, stSectionFilters.mxRecords],
  );

  const subdomainsDisplayList = useMemo(
    () =>
      filteredSubdomainsList.filter((row) => securityTrailsCardTextMatch(stSectionFilters.subdomains, row.hostname)),
    [filteredSubdomainsList, stSectionFilters.subdomains],
  );

  const domainsOnIpFilterOptions = useMemo(
    () => buildDomainsOnIpFilterOptions(details?.ipAddresses, details?.domainsOnIp),
    [details?.ipAddresses, details?.domainsOnIp],
  );

  const domainsOnNsFilterOptions = useMemo(
    () => buildDomainsOnNsFilterOptions(details?.nameservers, details?.domainsOnNs),
    [details?.nameservers, details?.domainsOnNs],
  );

  const siblingFilteredDomainsOnIpList = useMemo(
    () => filterDomainsOnIpBySiblingIp(filteredDomainsOnIpList, domainsOnIpSiblingFilter),
    [filteredDomainsOnIpList, domainsOnIpSiblingFilter],
  );

  const nameserverFilteredDomainsOnNsList = useMemo(
    () => filterDomainsOnNsByNameserver(filteredDomainsOnNsList, domainsOnNsNameserverFilter),
    [filteredDomainsOnNsList, domainsOnNsNameserverFilter],
  );

  const domainsOnIpDisplayList = useMemo(
    () =>
      siblingFilteredDomainsOnIpList.filter((row) =>
        securityTrailsCardTextMatch(
          stSectionFilters.domainsOnIp,
          `${row.relatedDomain} ${row.targetIp} ${domainsListMetadataSearchText(row)}`,
        ),
      ),
    [siblingFilteredDomainsOnIpList, stSectionFilters.domainsOnIp],
  );

  const domainsOnNsDisplayList = useMemo(
    () =>
      nameserverFilteredDomainsOnNsList.filter((row) =>
        securityTrailsCardTextMatch(
          stSectionFilters.domainsOnNs,
          `${row.relatedDomain} ${row.nameserver} ${domainsListMetadataSearchText(row)}`,
        ),
      ),
    [nameserverFilteredDomainsOnNsList, stSectionFilters.domainsOnNs],
  );

  const certificatesForDisplay = useMemo(() => {
    if (!filteredCertificates.length) return [];
    if (!kwActive) return filteredCertificates;
    return filteredCertificates.filter((cert) => {
      if (objectMatchesKeywordFilters(cert, keywordFilterQuery)) return true;
      return cert.subjectAltNames?.some((san) => objectMatchesKeywordFilters(san, keywordFilterQuery));
    });
  }, [filteredCertificates, keywordFilterQuery, kwActive]);

  /** IDs of displayed certificates that have a SANs list to expand. */
  const expandableSanCertIds = useMemo(
    () => certificatesForDisplay.filter((cert) => cert.subjectAltNames?.length > 0).map((cert) => cert.id),
    [certificatesForDisplay],
  );
  const allSansExpanded =
    expandableSanCertIds.length > 0 && expandableSanCertIds.every((id) => expandedSanCertIds.has(id));

  const toggleAllCertSans = () => {
    setExpandedSanCertIds(allSansExpanded ? new Set() : new Set(expandableSanCertIds));
  };

  useEffect(() => {
    setExpandedSanCertIds(new Set());
  }, [investigationId]);

  const findingsForDisplay = useMemo(() => {
    if (!filteredFindings.length) return [];
    if (!kwActive) return filteredFindings;
    return filteredFindings.filter((f) => objectMatchesKeywordFilters(f, keywordFilterQuery));
  }, [filteredFindings, keywordFilterQuery, kwActive]);

  const [activeSource, setActiveSource] = useState('securitytrails');
  const homeSectionJumpAppliedRef = useRef(false);

  useEffect(() => {
    if (!findingsTabNavigateNonce) return;
    setActiveSource('manual');
  }, [findingsTabNavigateNonce]);

  // Scroll to a specific finding when arriving from home search panel navigation.
  useEffect(() => {
    if (!jumpToFindingId || loading) return;
    setActiveSource('manual');
    const tid = setTimeout(() => {
      const el = document.querySelector(`[data-finding-id="${jumpToFindingId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.outline = '2px solid #3b82f6';
        el.style.borderRadius = '6px';
        setTimeout(() => { el.style.outline = ''; el.style.borderRadius = ''; }, 2500);
      }
    }, 400);
    return () => clearTimeout(tid);
  }, [jumpToFindingId, loading, otherInformation]);

  // Open the matched tool section (WHOIS, MX, URLScan, …) after navigating from home search.
  const detailsReady = Boolean(details) && !loading;
  useEffect(() => {
    if (!jumpToSourceKind || jumpToSourceKind === 'OTHER_INFORMATION') return;
    if (!detailsReady || homeSectionJumpAppliedRef.current) return;
    homeSectionJumpAppliedRef.current = true;
    const dest = destinationForToolSourceKind(jumpToSourceKind, { details, needle: jumpToNeedle });
    setActiveSource(dest.tab);
    if (dest.tab === 'securitytrails' && dest.sectionId) {
      setSecurityTrailsOpenSectionIds(new Set([dest.sectionId]));
    }
    const needle = typeof jumpToNeedle === 'string' ? jumpToNeedle.trim() : '';
    if (needle && needle.length <= 80) {
      setKeywordFilterQuery({ terms: [needle], ops: [] });
    }
    if (dest.tab === 'crtsh') {
      const n = needle.toLowerCase();
      const matchingIds = (details.certificates || [])
        .filter((cert) => (cert.subjectAltNames || []).some((san) => (
          JSON.stringify(san).toLowerCase().includes(n)
        )))
        .map((cert) => cert.id)
        .filter(Boolean);
      const fallbackIds = (details.certificates || [])
        .filter((cert) => cert.subjectAltNames?.length > 0)
        .map((cert) => cert.id)
        .filter(Boolean);
      setExpandedSanCertIds(new Set(matchingIds.length ? matchingIds : fallbackIds));
    }
    if (!dest.sectionId) return undefined;
    return scheduleScrollAndHighlight(`[data-section-id="${dest.sectionId}"]`);
    // First ready snapshot only — later details refreshes must not cancel the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsReady, jumpToSourceKind, jumpToNeedle]);

  const hasSecurityTrailsTab = useMemo(() => {
    if (!details) return false;
    return !!(
      details.whoisRecord
      || details.historicalWhois?.length
      || details.nameservers?.length
      || details.ipAddresses?.length
      || details.soaRecords?.length
      || details.txtRecords?.length
      || details.mxRecords?.length
      || details.subdomains?.length
      || details.domainsOnIp?.length
      || details.domainsOnNs?.length
    );
  }, [details]);

  const hasSecurityTrailsHistoricalTab = useMemo(() => {
    if (!details) return false;
    return (details.historicalDnsRecords?.length || 0) > 0;
  }, [details]);

  /** Home preview: show tab when there is data or an outcome. Case view: always show so analysts see “not triggered” states. */
  const showSecurityTrailsTab = useMemo(
    () =>
      !isPreviewMode ||
      hasSecurityTrailsTab ||
      !!toolOutcomeByKind(details?.toolOutcomes, 'SECURITY_TRAILS'),
    [isPreviewMode, hasSecurityTrailsTab, details?.toolOutcomes],
  );

  const showSecurityTrailsHistoricalTab = useMemo(
    () =>
      !isPreviewMode ||
      hasSecurityTrailsHistoricalTab ||
      !!toolOutcomeByKind(details?.toolOutcomes, 'SECURITY_TRAILS'),
    [isPreviewMode, hasSecurityTrailsHistoricalTab, details?.toolOutcomes],
  );

  /** Same four source tabs as Home keyword search; always shown (case page already did via !isPreviewMode). */
  const showUrlScanTab = true;
  const showPublicWwwTab = true;

  const showCrtTab = useMemo(
    () =>
      !isPreviewMode ||
      (details?.certificates?.length || 0) > 0 ||
      !!toolOutcomeByKind(details?.toolOutcomes, 'CRT_SH'),
    [isPreviewMode, details?.certificates, details?.toolOutcomes],
  );

  /** Home domain preview: automated source tabs only; findings belong on the saved case. */
  const showFindingsTab = !isPreviewMode;

  const invStatusUpper = String(details?.status || '').toUpperCase();
  const investigationTerminal = invStatusUpper === 'COMPLETED' || invStatusUpper === 'FAILED';
  const workflowFlags = details?.workflowRequestedTools;

  const toolNotRunCaseCopy = (flagKey) => {
    if (isPreviewMode || !investigationTerminal || !workflowFlags || workflowFlags[flagKey] !== false) {
      return false;
    }
    // Placeholder nodes store all flags false; after Investigate + re-run, data may exist before flags refresh in UI.
    if (flagKey === 'enableSecurityTrails' && hasSecurityTrailsTab) return false;
    if (flagKey === 'enableCrtSh' && (details?.certificates?.length || 0) > 0) return false;
    if (flagKey === 'enablePublicWww' && (details?.publicWwwResults?.length || 0) > 0) return false;
    if (flagKey === 'enableUrlScan' && (details?.urlScanPreviewRows?.length || 0) > 0) return false;
    return true;
  };

  const effectiveInvestigationId = investigationId || details?.investigationId;
  const crtShOutcome = toolOutcomeByKind(details?.toolOutcomes, 'CRT_SH');
  const crtShWasRequested = !toolNotRunCaseCopy('enableCrtSh');
  const crtShAttemptFinished = isPreviewMode
    ? (crtShOutcome != null || !streamingPreview)
    : (crtShWasRequested && investigationTerminal);
  const showCrtShRerunPrompt =
    !!effectiveInvestigationId &&
    crtShWasRequested &&
    crtShAttemptFinished &&
    (details?.certificates?.length || 0) === 0;

  const urlScanEmptyHint = useMemo(() => {
    if (isPreviewMode && streamingPreview) {
      const scanO = toolOutcomeByKind(details?.toolOutcomes, 'URL_SCAN_SCAN');
      const rows = details?.urlScanPreviewRows ?? [];
      if (!scanO && rows.length === 0) {
        return 'URLScan direct scan is still running on the server. Rows will appear here as they are received (this often takes 30 seconds to several minutes). The Domain preview panel above shows live status — an empty table here does not mean the scan failed.';
      }
    }
    const parts = [toolOutcomeByKind(details?.toolOutcomes, 'URL_SCAN_SCAN'), toolOutcomeByKind(details?.toolOutcomes, 'URL_SCAN_SEARCH')]
      .filter((o) => o && o.status && o.status !== 'OK')
      .map((o) => o.message);
    return parts.length ? parts.join(' ') : null;
  }, [details?.toolOutcomes, details?.urlScanPreviewRows, isPreviewMode, streamingPreview]);

  const publicWwwEmptyHint = useMemo(() => {
    const o = toolOutcomeByKind(details?.toolOutcomes, 'PUBLIC_WWW');
    return o && o.status !== 'OK' ? o.message : null;
  }, [details?.toolOutcomes]);

  useEffect(() => {
    if (!isPreviewMode || !details) return;
    const pw = details.publicWwwResults?.length > 0;
    if (pw && !hasSecurityTrailsTab && !(details.certificates?.length > 0)) {
      setActiveSource('publicwww');
    }
  }, [isPreviewMode, details, hasSecurityTrailsTab]);

  useEffect(() => {
    if (!isPreviewMode || activeSource !== 'manual') return;
    if (showSecurityTrailsTab) setActiveSource('securitytrails');
    else if (showUrlScanTab) setActiveSource('urlscan');
    else if (showPublicWwwTab) setActiveSource('publicwww');
    else if (showCrtTab) setActiveSource('crtsh');
  }, [isPreviewMode, activeSource, showSecurityTrailsTab, showUrlScanTab, showPublicWwwTab, showCrtTab]);

  const [paginationState, setPaginationState] = useState({
    historicalWhois:  { currentPage: 1, itemsPerPage: 20 },
    nameservers:      { currentPage: 1, itemsPerPage: 20 },
    aRecords:         { currentPage: 1, itemsPerPage: 20 },
    aaaaRecords:      { currentPage: 1, itemsPerPage: 20 },
    soaRecords:       { currentPage: 1, itemsPerPage: 20 },
    txtRecords:       { currentPage: 1, itemsPerPage: 20 },
    mxRecords:        { currentPage: 1, itemsPerPage: 20 },
    subdomains:       { currentPage: 1, itemsPerPage: 20 },
    domainsOnIp:      { currentPage: 1, itemsPerPage: 20 },
    domainsOnNs:      { currentPage: 1, itemsPerPage: 20 },
    certificates:     { currentPage: 1, itemsPerPage: 20 },
    manualFindings:   { currentPage: 1, itemsPerPage: 20 },
  });

  const updatePagination = (listName, currentPage) =>
    setPaginationState(prev => ({ ...prev, [listName]: { ...prev[listName], currentPage } }));

  const updatePageSize = (listName, itemsPerPage) =>
    setPaginationState(prev => ({ ...prev, [listName]: { currentPage: 1, itemsPerPage } }));

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, certificates: { ...prev.certificates, currentPage: 1 } }));
  }, [crtFilter]);

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, manualFindings: { ...prev.manualFindings, currentPage: 1 } }));
  }, [findingTypeFilter]);

  useEffect(() => {
    setDomainsOnIpSiblingFilter('ALL');
    setDomainsOnNsNameserverFilter('ALL');
  }, [investigationId]);

  useEffect(() => {
    if (
      domainsOnIpSiblingFilter !== 'ALL'
      && !domainsOnIpFilterOptions.some((option) => option.value === domainsOnIpSiblingFilter)
    ) {
      setDomainsOnIpSiblingFilter('ALL');
    }
  }, [domainsOnIpFilterOptions, domainsOnIpSiblingFilter]);

  useEffect(() => {
    if (
      domainsOnNsNameserverFilter !== 'ALL'
      && !domainsOnNsFilterOptions.some((option) => option.value === domainsOnNsNameserverFilter)
    ) {
      setDomainsOnNsNameserverFilter('ALL');
    }
  }, [domainsOnNsFilterOptions, domainsOnNsNameserverFilter]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      domainsOnIp: { ...prev.domainsOnIp, currentPage: 1 },
    }));
  }, [domainsOnIpSiblingFilter]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      domainsOnNs: { ...prev.domainsOnNs, currentPage: 1 },
    }));
  }, [domainsOnNsNameserverFilter]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      historicalWhois: { ...prev.historicalWhois, currentPage: 1 },
      nameservers: { ...prev.nameservers, currentPage: 1 },
      aRecords: { ...prev.aRecords, currentPage: 1 },
      aaaaRecords: { ...prev.aaaaRecords, currentPage: 1 },
      soaRecords: { ...prev.soaRecords, currentPage: 1 },
      txtRecords: { ...prev.txtRecords, currentPage: 1 },
      mxRecords: { ...prev.mxRecords, currentPage: 1 },
      subdomains: { ...prev.subdomains, currentPage: 1 },
      domainsOnIp: { ...prev.domainsOnIp, currentPage: 1 },
      domainsOnNs: { ...prev.domainsOnNs, currentPage: 1 },
      certificates: { ...prev.certificates, currentPage: 1 },
      manualFindings: { ...prev.manualFindings, currentPage: 1 },
    }));
  }, [keywordFilterQuery]);

  const getPaginatedItems = (items, listName) => {
    if (!items || !Array.isArray(items)) return [];
    const { currentPage, itemsPerPage } = paginationState[listName];
    return items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  };

  const loadCaseGraphLinks = async () => {
    if (isPreviewMode || !caseId) return;
    try {
      const res = await axios.get(`/api/investigations/case/${caseId}/graph-links`);
      setCaseGraphLinks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setCaseGraphLinks([]);
    }
  };

  const loadDetails = async ({ soft = false } = {}) => {
    if (isPreviewMode) return;
    try {
      if (soft) {
        setDetailsRefreshing(true);
      } else {
        setLoading(true);
      }
      const [detailsRes, otherInfoRes, linkagesRes] = await Promise.all([
        axios.get(`/api/investigations/${investigationId}/details`),
        axios.get(`/api/investigations/${investigationId}/other-information`),
        axios.get(`/api/investigations/${investigationId}/linkages`),
      ]);
      setDetails(detailsRes.data);
      setOtherInformation(otherInfoRes.data);
      setLinkages(Array.isArray(linkagesRes.data) ? linkagesRes.data : []);
      await loadCaseGraphLinks();
      setError(null);
    } catch (e) {
      console.error(e);
      setError('Failed to load node details');
    } finally {
      if (soft) {
        setDetailsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const refreshFindingsAndNetwork = async () => {
    if (isPreviewMode) return;
    await loadDetails({ soft: true });
    await onInvestigationsUpdated?.();
  };

  useEffect(() => {
    if (isPreviewMode || caseWorkflowMaskActive) return;
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId, caseWorkflowMaskActive, isPreviewMode]);

  useEffect(() => {
    if (!isPreviewMode && caseId) loadCaseGraphLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, isPreviewMode]);

  const prevInvestigationStatusRef = useRef(null);
  const prevDetailsStatusRef = useRef(null);
  useEffect(() => {
    prevInvestigationStatusRef.current = null;
    prevDetailsStatusRef.current = null;
  }, [investigationId]);

  useEffect(() => {
    if (isPreviewMode || !investigationId) return;
    const listCur = String(investigationStatus ?? '').toUpperCase();
    const detailCur = String(details?.status ?? '').toUpperCase();
    const prevList = prevInvestigationStatusRef.current;
    const prevDetail = prevDetailsStatusRef.current;
    prevInvestigationStatusRef.current = investigationStatus;
    prevDetailsStatusRef.current = details?.status;

    const terminal = (s) => s === 'COMPLETED' || s === 'FAILED';
    const busy = (s) => s === 'PENDING' || s === 'RUNNING';
    const prevListU = prevList != null ? String(prevList).toUpperCase() : '';
    const prevDetailU = prevDetail != null ? String(prevDetail).toUpperCase() : '';

    if (busy(prevListU) && terminal(listCur)) {
      loadDetails({ soft: true });
      return;
    }
    if (busy(prevDetailU) && terminal(detailCur)) {
      loadDetails({ soft: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDetails; intentional mirror of investigationId-only load effect
  }, [investigationStatus, details?.status, investigationId, isPreviewMode]);

  const prevWorkflowMaskActiveRef = useRef(false);
  useEffect(() => {
    if (isPreviewMode || !investigationId) return;
    if (prevWorkflowMaskActiveRef.current && !caseWorkflowMaskActive) {
      loadDetails({ soft: true });
    }
    prevWorkflowMaskActiveRef.current = caseWorkflowMaskActive;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh once when mask dismisses
  }, [caseWorkflowMaskActive, isPreviewMode, investigationId]);

  useEffect(() => {
    if (isPreviewMode || !caseWorkflowMaskActive || !workflowDetailsSnapshot) return;
    setDetails((prev) => mergeRefreshedDetails(workflowDetailsSnapshot, prev));
  }, [workflowDetailsSnapshot, caseWorkflowMaskActive, isPreviewMode]);

  const handleRerun = () => {
    setTargetDomainForInvestigation(details?.targetValue || '');
    setIsRerunMode(true);
    setShowInvestigationModal(true);
  };

  const handleRefresh = async () => {
    setSuccessMessage(null);
    await loadDetails({ soft: true });
  };

  const mergeRefreshedDetails = (data, prevDetails) => ({
    ...prevDetails,
    ...data,
    urlScanPreviewRows: data.urlScanPreviewRows ?? prevDetails?.urlScanPreviewRows,
  });

  const handleRerunCrtSh = async () => {
    const invId = effectiveInvestigationId;
    if (!invId || crtShRerunning) return;
    setCrtShRerunning(true);
    setError(null);
    try {
      await axios.post(`/api/investigations/${invId}/rerun`, {
        enableDomainIq: false,
        enableSecurityTrails: false,
        enableCrtSh: true,
        enableUrlScan: false,
        enableUrlScanSearch: false,
        enablePublicWww: false,
      });
      setDetails((prev) => (prev ? { ...prev, status: 'RUNNING', completedAt: null, toolOutcomes: [] } : prev));
      onRerunWorkflowPosted?.(invId);
      const started = Date.now();
      const maxWaitMs = 5 * 60 * 1000;
      while (Date.now() - started < maxWaitMs) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await axios.get(`/api/investigations/${invId}/details`);
        const data = res.data;
        const status = String(data?.status || '').toUpperCase();
        if (status === 'COMPLETED' || status === 'FAILED') {
          if (isPreviewMode) {
            setDetails((prev) => {
              const merged = mergeRefreshedDetails(data, prev);
              onPreviewDataUpdated?.(merged);
              return merged;
            });
          } else {
            await loadDetails({ soft: true });
          }
          break;
        }
      }
    } catch (e) {
      setError('Failed to re-run crt.sh: ' + (e.response?.data?.message || e.message));
    } finally {
      setCrtShRerunning(false);
    }
  };

  const handleRunPublicWww = async () => {
    if (!investigationId || publicWwwRunning || isPreviewMode) return;
    const publicWwwOnlyFlags = {
      enableDomainIq: false,
      enableSecurityTrails: false,
      enableCrtSh: false,
      enableUrlScan: false,
      enableUrlScanSearch: false,
      enablePublicWww: true,
    };
    setPublicWwwRunning(true);
    setError(null);
    try {
      await axios.post(`/api/investigations/${investigationId}/rerun`, publicWwwOnlyFlags);
      setDetails((prev) => (prev ? { ...prev, status: 'RUNNING', completedAt: null, toolOutcomes: [] } : prev));
      onRerunWorkflowPosted?.(investigationId);
      onInvestigationWorkflowToolsSelected?.(publicWwwOnlyFlags);
      await onInvestigationsUpdated?.();
      const started = Date.now();
      const maxWaitMs = 5 * 60 * 1000;
      while (Date.now() - started < maxWaitMs) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await axios.get(`/api/investigations/${investigationId}/details`);
        const data = res.data;
        const status = String(data?.status || '').toUpperCase();
        if (status === 'COMPLETED' || status === 'FAILED') {
          await loadDetails({ soft: true });
          setPublicWwwRefreshNonce((n) => n + 1);
          break;
        }
      }
    } catch (e) {
      setError('Failed to run PublicWWW: ' + (e.response?.data?.message || e.message));
    } finally {
      setPublicWwwRunning(false);
    }
  };

  const handleInvestigate = (targetDomain, deriveFrom = null, connectionLabel = undefined, options = null) => {
    if (isPreviewMode && typeof onPreviewContinue === 'function') {
      onPreviewContinue(targetDomain, deriveFrom);
      return;
    }
    if (isPreviewMode) return;
    setTargetDomainForInvestigation(targetDomain);
    setDeriveFromValue(deriveFrom);
    setInvestigationModalConnectionLabel(
      connectionLabel != null && String(connectionLabel).trim() !== '' ? String(connectionLabel).trim() : null,
    );
    setInvestigateLaunchContext(options?.launchFrom === 'urlscan' ? 'urlscan' : null);
    setShowInvestigationModal(true);
  };

  const handleAddPlaceholderNode = async (targetDomain, deriveFromVal, connectionLabelArg) => {
    if (isPreviewMode || !investigationId) return;
    try {
      setError(null);
      const trimmedLabel =
        connectionLabelArg != null && String(connectionLabelArg).trim() !== '' ? String(connectionLabelArg).trim() : undefined;
      const res = await axios.post(`/api/investigations/${investigationId}/placeholder`, {
        targetDomain,
        deriveFrom: deriveFromVal ?? null,
        ...(trimmedLabel ? { connectionLabel: trimmedLabel } : {}),
      });
      const newId = res.data?.id;
      const commonLink = res.data?.commonLinkCreated === true;
      if (commonLink) {
        setSuccessMessage(
          `This target was already on the node map. A supplemental link was added to "${targetDomain}" (Common link identified).`,
        );
      } else {
        setSuccessMessage(`Added "${targetDomain}" to the network map.`);
      }
      if (onInvestigationsUpdated) {
        await onInvestigationsUpdated(newId);
      }
      await loadCaseGraphLinks();
    } catch (e) {
      setError('Could not add node: ' + (e.response?.data?.message || e.message));
    }
  };

  const commitKeywordFilterDraft = () => {
    const t = keywordFilterDraft.trim();
    if (!t) return;
    setKeywordFilterQuery((prev) => {
      if (prev.terms.some((p) => p.toLowerCase() === t.toLowerCase())) return prev;
      const terms = [...prev.terms, t];
      const ops = prev.terms.length === 0 ? [] : [...prev.ops, 'AND'];
      return { terms, ops: ops.slice(0, Math.max(0, terms.length - 1)) };
    });
    setKeywordFilterDraft('');
  };

  const setKeywordJoinOp = (opIndex, op) => {
    setKeywordFilterQuery((prev) => {
      const ops = [...prev.ops];
      if (opIndex < 0 || opIndex >= ops.length) return prev;
      ops[opIndex] = op === 'OR' ? 'OR' : 'AND';
      return { ...prev, ops };
    });
  };

  const removeKeywordTermAt = (index) => {
    setKeywordFilterQuery((prev) => {
      const { terms, ops } = prev;
      if (index < 0 || index >= terms.length) return prev;
      const newTerms = terms.filter((_, i) => i !== index);
      let newOps;
      if (terms.length <= 1) {
        newOps = [];
      } else if (index === 0) {
        newOps = ops.slice(1);
      } else if (index === terms.length - 1) {
        newOps = ops.slice(0, -1);
      } else {
        newOps = [...ops.slice(0, index - 1), ...ops.slice(index)];
      }
      return { terms: newTerms, ops: newOps };
    });
  };

  const clearKeywordFilters = () => {
    setKeywordFilterQuery(emptyKeywordQuery());
    setKeywordFilterDraft('');
  };

  const handleInvestigationWithTools = async (targetDomain, selectedTools) => {
    try {
      setError(null);
      setSuccessMessage(null);
      const enabledTools = [];
      if (selectedTools.securitytrails) enabledTools.push('SecurityTrails');
      if (selectedTools.crtsh) enabledTools.push('Crt.sh');
      if (selectedTools.urlscan) enabledTools.push('URLScan (Scan)');
      if (selectedTools.urlscanSearch) enabledTools.push('URLScan (Search)');
      if (selectedTools.publicwww) {
        enabledTools.push(selectedTools.publicWwwExactKeyword ? 'PublicWWW (exact keyword)' : 'PublicWWW');
      }

      if (isRerunMode) {
        setRerunning(true);
        const wf = {
          enableDomainIq: false,
          enableSecurityTrails: selectedTools.securitytrails,
          enableCrtSh: selectedTools.crtsh,
          enableUrlScan: selectedTools.urlscan,
          enableUrlScanSearch: selectedTools.urlscanSearch,
          enablePublicWww: selectedTools.publicwww,
          publicWwwExactKeyword: Boolean(selectedTools.publicwww && selectedTools.publicWwwExactKeyword),
        };
        await axios.post(`/api/investigations/${investigationId}/rerun`, wf);
        setDetails((prev) => (prev ? { ...prev, status: 'RUNNING', completedAt: null, toolOutcomes: [] } : prev));
        onRerunWorkflowPosted?.(investigationId);
        // Match Investigate-trigger: HUD + parents poll statuses so RUNNING is not stuck on stale COMPLETED.
        onInvestigationWorkflowToolsSelected?.(wf);
        await onInvestigationsUpdated?.();
        await loadDetails({ soft: true });
        setSuccessMessage(
          `Re-run started for ${targetDomain} (${enabledTools.join(', ')}). The progress overlay runs until tools finish; tab data refreshes automatically when each step completes.`,
        );
        setRerunning(false);
        setIsRerunMode(false);
      } else {
        let deriveFromForTrigger = deriveFromValue;
        let modalConnectionTrim =
          investigationModalConnectionLabel != null && String(investigationModalConnectionLabel).trim() !== ''
            ? String(investigationModalConnectionLabel).trim()
            : undefined;
        if (investigateLaunchContext === 'urlscan') {
          const scan = selectedTools.urlscan;
          const search = selectedTools.urlscanSearch;
          // One child edge: if both UrlScan tools are on, prefer Scan (Search+Scan dedupe is a separate backlog item).
          if (scan && !search) deriveFromForTrigger = 'URL_SCAN';
          else if (search && !scan) deriveFromForTrigger = 'URL_SEARCH';
          else if (scan && search) deriveFromForTrigger = 'URL_SCAN';
          modalConnectionTrim =
            deriveFromForTrigger === 'URL_SEARCH' ? 'URLScan Search' : 'URLScan Scan';
        }
        const triggerRes = await axios.post(`/api/investigations/${investigationId}/trigger`, {
          targetDomain,
          enableDomainIq: false,
          enableSecurityTrails: selectedTools.securitytrails,
          enableCrtSh: selectedTools.crtsh,
          enableUrlScan: selectedTools.urlscan,
          enableUrlScanSearch: selectedTools.urlscanSearch,
          enablePublicWww: selectedTools.publicwww,
          publicWwwExactKeyword: Boolean(selectedTools.publicwww && selectedTools.publicWwwExactKeyword),
          deriveFrom: deriveFromForTrigger,
          ...(modalConnectionTrim ? { connectionLabel: modalConnectionTrim } : {}),
        });
        onInvestigationWorkflowToolsSelected?.({
          enableDomainIq: false,
          enableSecurityTrails: selectedTools.securitytrails,
          enableCrtSh: selectedTools.crtsh,
          enableUrlScan: selectedTools.urlscan,
          enableUrlScanSearch: selectedTools.urlscanSearch,
          enablePublicWww: selectedTools.publicwww,
          publicWwwExactKeyword: Boolean(selectedTools.publicwww && selectedTools.publicWwwExactKeyword),
        });
        if (enabledTools.length > 0) {
          onRerunWorkflowPosted?.(triggerRes.data?.id ?? investigationId);
        }
        setInvestigateLaunchContext(null);
        setInvestigationModalConnectionLabel(null);
        const commonLink = triggerRes.data?.commonLinkCreated === true;
        if (commonLink) {
          setSuccessMessage(
            enabledTools.length > 0
              ? `Target "${targetDomain}" was already on this case. A link was added and node started with: ${enabledTools.join(', ')}.`
              : `Target "${targetDomain}" was already on this case. Linked from this node (Common link identified). Re-run from the node card if you need fresh tool data.`,
          );
        } else {
          setSuccessMessage(`Node started for ${targetDomain} with: ${enabledTools.join(', ')}!`);
        }
        const newInvId = triggerRes.data?.id;
        if (onInvestigationsUpdated && newInvId) {
          await onInvestigationsUpdated(newInvId);
        }
      }
    } catch (e) {
      console.error(e);
      setRerunning(false);
      setError((isRerunMode ? 'Failed to re-run: ' : 'Failed to start: ') + (e.response?.data?.message || e.message));
    }
  };

  const handleDeleteOtherInformation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this finding?')) return;
    try {
      await axios.delete(`/api/investigations/${investigationId}/other-information/${id}`);
      setSuccessMessage('Finding deleted successfully');
      await refreshFindingsAndNetwork();
    } catch (e) {
      setError('Failed to delete finding: ' + (e.response?.data?.message || e.message));
    }
  };

  const findLinkageForValue = (value) => {
    if (!value) return null;
    return linkages.find(l => (l.matchedValue || '').toLowerCase() === (value || '').toLowerCase());
  };

  const normalizeMapTarget = (v) => (v == null ? '' : String(v).trim().toLowerCase());

  /** True if this string already matches an investigation node’s target on the case map. */
  const isTargetAlreadyOnCaseMap = useMemo(() => {
    const keys = new Set(
      (caseInvestigations || []).map((inv) => normalizeMapTarget(inv.targetValue)).filter(Boolean),
    );
    return (value) => {
      const k = normalizeMapTarget(value);
      return k !== '' && keys.has(k);
    };
  }, [caseInvestigations]);

  const previewContinueActive = isPreviewMode && typeof onPreviewContinue === 'function';
  const showInvestigationPair = !isPreviewMode || previewContinueActive;
  const pairAddNodeOnly = !isPreviewMode ? handleAddPlaceholderNode : undefined;

  const securityTrailsDense = !isPreviewMode && securityTrailsViewDensity === 'compact';
  const urlScanViewDensity = 'comfortable';
  const publicWwwViewDensity = 'comfortable';
  const crtDense = false;
  const manualDense = false;

  if (loading) {
    return (
      <PdsBox justifyContent="center" padding="lg">
        <PdsLoader size="md" />
      </PdsBox>
    );
  }

  if (error) {
    return <PdsNotification color="warning" dismissible={false} style={{ marginBottom: 16 }}>{error}</PdsNotification>;
  }

  if (!details) {
    return <PdsMantineText type="body" color="neutral">No node data available</PdsMantineText>;
  }

  return (
    <PdsBox
      id={`investigation-${investigationId}`}
      data-investigation-details
      direction="column"
      gap="md"
      style={{ marginTop: 32, paddingTop: 16, borderTop: '2px solid #d0d7de', scrollMarginTop: 20 }}
    >
      {/* Investigation header */}
      {details.targetValue && (
        <PdsBox
          direction="row"
          alignItems="center"
          gap="sm"
          style={{ flexWrap: 'wrap' }}
          className="investigation-details-heading"
        >
          <PdsSearchIcon color="action" size="sm" style={{ flexShrink: 0 }} />
          <span className="investigation-details-heading-inner">
            <span className="argus-type-section-heading">Current Node:</span>
            <span className="argus-data argus-data-focal investigation-details-heading-target">
              {details.targetValue}
            </span>
          </span>
        </PdsBox>
      )}

      {/* Action bar - hidden in preview mode */}
      {!isPreviewMode && (
      <PdsBox direction="row" justifyContent="space-between" alignItems="center" wrap="wrap" gap="sm">
        <PdsBox direction="row" gap="sm">
          <PdsMantineButton
            rank="outline"
            color="neutral"
            onClick={handleRerun}
            disabled={rerunning}
            leadingIcon={rerunning ? <PdsSpinnerIcon /> : <PdsSyncIcon />}
          >
            {rerunning ? 'Re-running...' : 'Re-run tools'}
          </PdsMantineButton>
          {!caseWorkflowMaskActive && (
          <PdsMantineButton
            rank="fill"
            color="action"
            onClick={handleRefresh}
            disabled={loading || detailsRefreshing}
            leadingIcon={detailsRefreshing ? <PdsSpinnerIcon /> : <PdsSyncIcon />}
          >
            {detailsRefreshing ? 'Refreshing...' : 'Refresh'}
          </PdsMantineButton>
          )}
          <PdsMantineButton
            rank="fill"
            color="success"
            onClick={() => setShowAddFindingModal(true)}
            leadingIcon={<PdsPlusAddIcon />}
          >
            Add Finding
          </PdsMantineButton>
          <PdsMantineButton
            rank="outline"
            color="action"
            onClick={() => setShowExportModal(true)}
            leadingIcon={<PdsDocumentDownloadIcon />}
          >
            Export
          </PdsMantineButton>
        </PdsBox>

        {successMessage && (
          <PdsNotification color="success" onDismiss={() => setSuccessMessage(null)} size="sm">
            {successMessage}
          </PdsNotification>
        )}
        {exportError && (
          <PdsNotification color="warning" onDismiss={() => setExportError(null)} size="sm">
            {exportError}
          </PdsNotification>
        )}
      </PdsBox>
      )}

      {/* ── Source tabs ── */}
      <PdsBox direction="column" gap="md">

        {/* Tab bar */}
        <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center" style={{ borderBottom: '2px solid #e1e4e8' }}>
            {showSecurityTrailsTab && (
              <PdsTab value="securitytrails" selected={activeSource === 'securitytrails'} color="action" onClick={() => setActiveSource('securitytrails')} leadingIcon={<PdsSearchIcon />}>
                SecurityTrails
              </PdsTab>
            )}
            {showSecurityTrailsHistoricalTab && (
              <PdsTab value="securitytrails-historical" selected={activeSource === 'securitytrails-historical'} color="action" onClick={() => setActiveSource('securitytrails-historical')} leadingIcon={<PdsHistoryIcon />}>
                SecurityTrails (Historical)
              </PdsTab>
            )}
            {showUrlScanTab && (
              <PdsTab value="urlscan" selected={activeSource === 'urlscan'} color="action" onClick={() => setActiveSource('urlscan')} leadingIcon={<PdsGlobe2Icon />}>
                URLScan
              </PdsTab>
            )}
            {showPublicWwwTab && (
              <PdsTab value="publicwww" selected={activeSource === 'publicwww'} color="action" onClick={() => setActiveSource('publicwww')} leadingIcon={<PdsMonitorDesktopIcon />}>
                PublicWWW
              </PdsTab>
            )}
            {showCrtTab && (
              <PdsTab value="crtsh" selected={activeSource === 'crtsh'} color="action" onClick={() => setActiveSource('crtsh')} leadingIcon={<PdsShieldSecurityIcon />}>
                Certificate Transparency (crt.sh)
              </PdsTab>
            )}
            {showFindingsTab && (
              <PdsTab value="manual" selected={activeSource === 'manual'} color="action" onClick={() => setActiveSource('manual')} leadingIcon={<PdsWriteEditArticleIcon />}>
                Findings ({otherInformation?.length ?? 0})
              </PdsTab>
            )}
        </PdsBox>

        {/* Tab content */}
        <PdsBox direction="column" gap="md" style={{ paddingTop: 8 }}>

          {!isPreviewMode && (activeSource !== 'manual' || (otherInformation?.length ?? 0) > 0) && (
            <PdsBox
              direction="column"
              gap="xs"
              style={{
                padding: '10px 14px',
                background: '#fff',
                borderRadius: 8,
                border: '1px solid #d0d7de',
                boxShadow: '0 1px 0 rgba(27, 31, 36, 0.04)',
              }}
            >
              <PdsBox direction="row" alignItems="center" gap="sm" wrap="wrap" className="argus-keyword-result-search">
                <PdsFilterIcon color="neutral" size="sm" style={{ flexShrink: 0 }} />
                <PdsBox
                  direction="row"
                  alignItems="center"
                  gap="xs"
                  wrap="wrap"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: '#f6f8fa',
                    border: '1px solid #e1e4e8',
                  }}
                >
                  {keywordFilterQuery.terms.map((word, i) => (
                    <React.Fragment key={`${word}-${i}`}>
                      {i > 0 && (
                        <KeywordJoinToggle value={keywordFilterQuery.ops[i - 1] || 'AND'} onChange={(op) => setKeywordJoinOp(i - 1, op)} />
                      )}
                      <PdsBox
                        direction="row"
                        alignItems="center"
                        gap="xs"
                        style={{
                          background: '#ddf4ff',
                          borderRadius: 6,
                          padding: '2px 8px',
                          border: '1px solid #b6e3ff',
                        }}
                      >
                        <PdsMantineText type="textline" component="span" fontWeight="strong">{word}</PdsMantineText>
                        <button
                          type="button"
                          onClick={() => removeKeywordTermAt(i)}
                          aria-label={`Remove term ${word}`}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 1, padding: 0, fontSize: 16, color: '#57606a' }}
                        >
                          ×
                        </button>
                      </PdsBox>
                    </React.Fragment>
                  ))}
                  <PdsTextField
                    placeholder={
                      keywordFilterQuery.terms.length === 0
                        ? 'Search results in this tab…'
                        : 'Add another term (Enter)'
                    }
                    value={keywordFilterDraft}
                    onChange={(e) => setKeywordFilterDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitKeywordFilterDraft();
                      }
                    }}
                    stretch
                    style={{ flex: '1 1 180px', minWidth: 140 }}
                  />
                </PdsBox>
                {keywordFilterQuery.terms.length > 0 && (
                  <PdsMantineButton size="xs" rank="ghost" color="neutral" onClick={clearKeywordFilters}>
                    Clear
                  </PdsMantineButton>
                )}
              </PdsBox>
              <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                Case-insensitive match per term. With multiple terms, use <strong>AND</strong> (all must match) or <strong>OR</strong> (any
                may match) between each pair — combined left to right. Applies to {KEYWORD_FILTER_TAB_LABEL[activeSource] ?? 'this tab'}.
              </PdsMantineText>
            </PdsBox>
          )}

          {/* SecurityTrails */}
          {activeSource === 'securitytrails' && (
            <>
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'SECURITY_TRAILS')}
                streamingPreview={streamingPreview}
              />
              {toolNotRunCaseCopy('enableSecurityTrails') ? (
                <PdsMantineText type="body" color="neutral" style={{ marginBottom: 8, lineHeight: 1.55 }} component="p">
                  SecurityTrails was not run for this node. Use <strong>Re-run tools</strong> to include it.
                </PdsMantineText>
              ) : !hasSecurityTrailsTab ? (
                <PdsMantineText type="body" color="neutral" style={{ fontStyle: 'italic', marginBottom: 8 }} component="p">
                  No SecurityTrails DNS, WHOIS, or related rows are stored for this node yet. If a message appears
                  above, the lookup returned no data or failed for your query.
                </PdsMantineText>
              ) : null}
              {!toolNotRunCaseCopy('enableSecurityTrails') && (
              <>
              {!isPreviewMode && (
                <InvestigationDensityToolbar
                  density={securityTrailsViewDensity}
                  onDensityChange={setSecurityTrailsViewDensity}
                />
              )}
              <InvestigationSectionGrid>
              {/* Current WHOIS */}
              {details.whoisRecord && filteredWhoisVisible && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-whois-current"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="Current WHOIS"
                  icon={PdsDocument1Icon}
                >
                  <PdsBox direction="column" gap="xs">
                    {[
                      ['Domain', details.whoisRecord.domain],
                      ['Registrar', details.whoisRecord.registrar || 'N/A'],
                      ['Registrant', details.whoisRecord.regName || 'N/A'],
                      ['Email', details.whoisRecord.regEmail || 'N/A'],
                      ['Organization', details.whoisRecord.regOrg || 'N/A'],
                      ['Created', details.whoisRecord.createDate ? new Date(details.whoisRecord.createDate).toLocaleDateString() : 'N/A'],
                      ['Expires', details.whoisRecord.expireDate ? new Date(details.whoisRecord.expireDate).toLocaleDateString() : 'N/A'],
                      ['Status', details.whoisRecord.status || 'N/A'],
                    ].map(([label, value]) => (
                      <PdsBox key={label} direction="row" gap="xs">
                        <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-label">
                          {label}:
                        </PdsMantineText>
                        <PdsMantineText type="textline" component="span" className="argus-data">
                          {value}
                        </PdsMantineText>
                      </PdsBox>
                    ))}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* Historical WHOIS */}
              {details.historicalWhois?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-whois-historical"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="Historical WHOIS"
                  icon={PdsHistoryIcon}
                  count={historicalWhoisDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredHistoricalWhoisList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.historicalWhois}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, historicalWhois: e.target.value }));
                          updatePagination('historicalWhois', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredHistoricalWhoisList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : historicalWhoisDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(historicalWhoisDisplayList, 'historicalWhois').map((record, idx) => (
                      <InvestigationDenseRow
                        key={idx}
                        dense={securityTrailsDense}
                        variant="column"
                        initialExpanded
                        titleLine={`${record.registrar || 'Unknown Registrar'} · Mod: ${record.modDate ? new Date(record.modDate).toLocaleDateString() : 'N/A'}`}
                        actions={null}
                      >
                        <PdsBox direction="row" gap="sm" alignItems="center">
                          <PdsMantineText type="textline" fontWeight="strong" component="span">{record.registrar || 'Unknown Registrar'}</PdsMantineText>
                          <PdsMantineText type="textline" color="neutral" component="span">
                            (Mod: {record.modDate ? new Date(record.modDate).toLocaleDateString() : 'N/A'})
                          </PdsMantineText>
                        </PdsBox>
                        <PdsMantineText type="textline" component="span"><strong>Name:</strong> {record.regName || 'N/A'} {record.regOrg && `(${record.regOrg})`}</PdsMantineText>
                        <PdsMantineText type="textline" component="span"><strong>Email:</strong> {record.regEmail || 'N/A'}</PdsMantineText>
                      </InvestigationDenseRow>
                    ))}
                    {historicalWhoisDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.historicalWhois.currentPage} totalItems={historicalWhoisDisplayList.length} itemsPerPage={paginationState.historicalWhois.itemsPerPage} onPageChange={(p) => updatePagination('historicalWhois', p)} onPageSizeChange={(s) => updatePageSize('historicalWhois', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* NS Records */}
              {details.nameservers?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-ns-records"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="NS Records"
                  icon={PdsServerIcon}
                  count={nameserversDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredNameserversList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.nameservers}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, nameservers: e.target.value }));
                          updatePagination('nameservers', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredNameserversList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : nameserversDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(nameserversDisplayList, 'nameservers').map((ns, idx) => (
                      <InvestigationDenseRow
                        key={idx}
                        dense={securityTrailsDense}
                        variant="column"
                        titleLine={ns.nameserver}
                        actions={
                          showInvestigationPair && isPlausibleInvestigateDomain(ns.nameserver) ? (
                            <InvestigatePairButtons
                              targetDomain={ns.nameserver}
                              deriveFrom="DOMAIN_ON_NS"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.DOMAIN_ON_NS}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(ns.nameserver)}
                            />
                          ) : null
                        }
                      >
                        <PdsMantineText type="textline" fontWeight="strong" component="span">{ns.nameserver}</PdsMantineText>
                        <PdsMantineText type="textline" color="neutral" component="span">{ns.domain}</PdsMantineText>
                      </InvestigationDenseRow>
                    ))}
                    {nameserversDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.nameservers.currentPage} totalItems={nameserversDisplayList.length} itemsPerPage={paginationState.nameservers.itemsPerPage} onPageChange={(p) => updatePagination('nameservers', p)} onPageSizeChange={(s) => updatePageSize('nameservers', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* A Records */}
              {filterARecords(details?.ipAddresses || []).length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-a-records"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="A Records"
                  icon={PdsNetworkIcon}
                  count={aRecordsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredARecordsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.aRecords}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, aRecords: e.target.value }));
                          updatePagination('aRecords', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredIpAddressesList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : aRecordsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(aRecordsDisplayList, 'aRecords').map((ip, idx) => {
                      const geo = details.ipGeolocations?.find(g => g.ipAddress === ip.ipAddress);
                      const geoBlock = geo ? (
                        <PdsBox direction="column" gap="xs">
                          <PdsMantineText type="textline" color="neutral" component="span">📍 {geo.city}, {geo.country}</PdsMantineText>
                          {geo.postalCode && <PdsMantineText type="textline" color="neutral" component="span"><strong>Postal:</strong> {geo.postalCode}</PdsMantineText>}
                          {geo.asn && <PdsMantineText type="textline" color="neutral" component="span"><strong>ASN:</strong> {geo.asn}</PdsMantineText>}
                        </PdsBox>
                      ) : (
                        <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No geolocation data</PdsMantineText>
                      );
                      return (
                        <InvestigationDenseRow
                          key={ip.ipAddress ?? idx}
                          dense={securityTrailsDense}
                          variant="row"
                          initialExpanded={ipGeoHasDisplayableGeoOrAsn(geo)}
                          titleLine={ip.ipAddress}
                          actions={showInvestigationPair ? (
                            <InvestigatePairButtons
                              targetDomain={ip.ipAddress}
                              deriveFrom="IP_ADDRESS"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.IP_ADDRESS}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(ip.ipAddress)}
                            />
                          ) : null}
                        >
                          {securityTrailsDense ? geoBlock : (
                            <>
                              <PdsMantineText type="textline" component="span" className="argus-data">{ip.ipAddress}</PdsMantineText>
                              {geoBlock}
                            </>
                          )}
                        </InvestigationDenseRow>
                      );
                    })}
                    {aRecordsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.aRecords.currentPage} totalItems={aRecordsDisplayList.length} itemsPerPage={paginationState.aRecords.itemsPerPage} onPageChange={(p) => updatePagination('aRecords', p)} onPageSizeChange={(s) => updatePageSize('aRecords', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* AAAA Records */}
              {filterAaaaRecords(details?.ipAddresses || []).length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-aaaa-records"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="AAAA Records"
                  icon={PdsNetworkIcon}
                  count={aaaaRecordsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredAaaaRecordsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.aaaaRecords}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, aaaaRecords: e.target.value }));
                          updatePagination('aaaaRecords', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredIpAddressesList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : aaaaRecordsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(aaaaRecordsDisplayList, 'aaaaRecords').map((ip, idx) => {
                      const geo = details.ipGeolocations?.find(g => g.ipAddress === ip.ipAddress);
                      const geoBlock = geo ? (
                        <PdsBox direction="column" gap="xs">
                          <PdsMantineText type="textline" color="neutral" component="span">📍 {geo.city}, {geo.country}</PdsMantineText>
                          {geo.postalCode && <PdsMantineText type="textline" color="neutral" component="span"><strong>Postal:</strong> {geo.postalCode}</PdsMantineText>}
                          {geo.asn && <PdsMantineText type="textline" color="neutral" component="span"><strong>ASN:</strong> {geo.asn}</PdsMantineText>}
                        </PdsBox>
                      ) : (
                        <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No geolocation data</PdsMantineText>
                      );
                      return (
                        <InvestigationDenseRow
                          key={ip.ipAddress ?? idx}
                          dense={securityTrailsDense}
                          variant="row"
                          initialExpanded={ipGeoHasDisplayableGeoOrAsn(geo)}
                          titleLine={ip.ipAddress}
                          actions={showInvestigationPair ? (
                            <InvestigatePairButtons
                              targetDomain={ip.ipAddress}
                              deriveFrom="IP_ADDRESS"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.IP_ADDRESS}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(ip.ipAddress)}
                            />
                          ) : null}
                        >
                          {securityTrailsDense ? geoBlock : (
                            <>
                              <PdsMantineText type="textline" component="span" className="argus-data">{ip.ipAddress}</PdsMantineText>
                              {geoBlock}
                            </>
                          )}
                        </InvestigationDenseRow>
                      );
                    })}
                    {aaaaRecordsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.aaaaRecords.currentPage} totalItems={aaaaRecordsDisplayList.length} itemsPerPage={paginationState.aaaaRecords.itemsPerPage} onPageChange={(p) => updatePagination('aaaaRecords', p)} onPageSizeChange={(s) => updatePageSize('aaaaRecords', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* SOA Records */}
              {details.soaRecords?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-soa-records"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="SOA Records"
                  icon={PdsServerIcon}
                  count={soaRecordsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredSoaRecordsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.soaRecords}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, soaRecords: e.target.value }));
                          updatePagination('soaRecords', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredSoaRecordsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : soaRecordsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(soaRecordsDisplayList, 'soaRecords').map((soa, idx) => (
                      <InvestigationDenseRow
                        key={soa.id ?? idx}
                        dense={securityTrailsDense}
                        variant="column"
                        titleLine={soaDenseTitleLine(soa)}
                        titleWordBreak
                      >
                        {!securityTrailsDense && (
                          <>
                            <PdsMantineText type="textline" component="span"><strong>MNAME:</strong> {soaPrimaryNameserver(soa) || 'N/A'}</PdsMantineText>
                            <PdsMantineText type="textline" component="span"><strong>RNAME:</strong> {soaAdminMailbox(soa) || 'N/A'}</PdsMantineText>
                            <PdsMantineText type="textline" color="neutral" component="span">
                              Serial: {soa.serial ?? 'N/A'} · Refresh: {soa.refresh ?? 'N/A'} · Retry: {soa.retry ?? 'N/A'} · Expire: {soa.expire ?? 'N/A'} · Minimum: {soa.minimum ?? 'N/A'}
                            </PdsMantineText>
                          </>
                        )}
                      </InvestigationDenseRow>
                    ))}
                    {soaRecordsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.soaRecords.currentPage} totalItems={soaRecordsDisplayList.length} itemsPerPage={paginationState.soaRecords.itemsPerPage} onPageChange={(p) => updatePagination('soaRecords', p)} onPageSizeChange={(s) => updatePageSize('soaRecords', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* TXT Records */}
              {details.txtRecords?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-txt-records"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="TXT Records"
                  icon={PdsDocument1Icon}
                  count={txtRecordsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredTxtRecordsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.txtRecords}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, txtRecords: e.target.value }));
                          updatePagination('txtRecords', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredTxtRecordsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : txtRecordsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(txtRecordsDisplayList, 'txtRecords').map((txt, idx) => (
                      <InvestigationDenseRow
                        key={txt.id ?? idx}
                        dense={securityTrailsDense}
                        variant="column"
                        titleLine={txt.txtValue || txt.domain}
                        titleWordBreak
                      >
                        {!securityTrailsDense && (
                          <PdsMantineText type="textline" component="span" className="argus-data" style={{ wordBreak: 'break-word', fontFamily: 'IBM Plex Mono, monospace' }}>
                            {txt.txtValue}
                          </PdsMantineText>
                        )}
                      </InvestigationDenseRow>
                    ))}
                    {txtRecordsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.txtRecords.currentPage} totalItems={txtRecordsDisplayList.length} itemsPerPage={paginationState.txtRecords.itemsPerPage} onPageChange={(p) => updatePagination('txtRecords', p)} onPageSizeChange={(s) => updatePageSize('txtRecords', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* MX Records */}
              {details.mxRecords?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-mx"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="MX Records"
                  icon={PdsEmailIcon}
                  count={mxRecordsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredMxRecordsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.mxRecords}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, mxRecords: e.target.value }));
                          updatePagination('mxRecords', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredMxRecordsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : mxRecordsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(mxRecordsDisplayList, 'mxRecords').map((mx, idx) => (
                      <InvestigationDenseRow
                        key={idx}
                        dense={securityTrailsDense}
                        variant="row"
                        titleLine={`${mx.priority} · ${mx.mxHost}`}
                        actions={showInvestigationPair ? (
                          <InvestigatePairButtons
                            targetDomain={mx.mxHost}
                            deriveFrom="MX_RECORD"
                            connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.MX_RECORD}
                            onRunTools={handleInvestigate}
                            onAddNodeOnly={pairAddNodeOnly}
                            showAddNode={!isTargetAlreadyOnCaseMap(mx.mxHost)}
                          />
                        ) : null}
                      >
                        <PdsBox direction="row" alignItems="center" gap="sm" flex="1">
                          <PdsTag color="action" dismissible={false} size="sm">{mx.priority}</PdsTag>
                          <PdsMantineText type="textline" component="span" className="argus-data">{mx.mxHost}</PdsMantineText>
                        </PdsBox>
                      </InvestigationDenseRow>
                    ))}
                    {mxRecordsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.mxRecords.currentPage} totalItems={mxRecordsDisplayList.length} itemsPerPage={paginationState.mxRecords.itemsPerPage} onPageChange={(p) => updatePagination('mxRecords', p)} onPageSizeChange={(s) => updatePageSize('mxRecords', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* Subdomains (SecurityTrails REST) */}
              {details.subdomains?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-subdomains"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="Subdomains"
                  icon={PdsSitemapIcon}
                  count={subdomainsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {filteredSubdomainsList.length > 1 && (
                      <PdsTextField
                        placeholder="Search within this section"
                        value={stSectionFilters.subdomains}
                        onChange={(e) => {
                          setStSectionFilters((p) => ({ ...p, subdomains: e.target.value }));
                          updatePagination('subdomains', 1);
                        }}
                        size="sm"
                        stretch
                      />
                    )}
                    {filteredSubdomainsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : subdomainsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(subdomainsDisplayList, 'subdomains').map((row, idx) => {
                      const lk = findLinkageForValue(row.hostname);
                      return (
                        <InvestigationDenseRow
                          key={idx}
                          dense={securityTrailsDense}
                          variant="row"
                          titleLine={row.hostname}
                          actions={showInvestigationPair ? (
                            <InvestigatePairButtons
                              targetDomain={row.hostname}
                              deriveFrom="SUBDOMAIN"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.SUBDOMAIN}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(row.hostname)}
                            />
                          ) : null}
                        >
                          <PdsBox direction="row" alignItems="center" gap="xs">
                            <PdsMantineText type="textline" component="span" className="argus-data">{row.hostname}</PdsMantineText>
                            {lk && (
                              <PdsMantineIconButton size="sm" rank="ghost" color="action" icon={<PdsLinkIcon />} onClick={() => setLinkageModal({ open: true, data: lk })} aria-label="Show linkage" />
                            )}
                          </PdsBox>
                        </InvestigationDenseRow>
                      );
                    })}
                    {subdomainsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.subdomains.currentPage} totalItems={subdomainsDisplayList.length} itemsPerPage={paginationState.subdomains.itemsPerPage} onPageChange={(p) => updatePagination('subdomains', p)} onPageSizeChange={(s) => updatePageSize('subdomains', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* Domains on Same IP */}
              {details.domainsOnIp?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-domains-ip"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="Domains on Same IP"
                  icon={PdsSitemapIcon}
                  count={domainsOnIpDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {(domainsOnIpFilterOptions.length > 0 || filteredDomainsOnIpList.length > 1) && (
                      <div
                        className={`argus-st-section-filter-row${
                          filteredDomainsOnIpList.length > 1 && domainsOnIpFilterOptions.length > 0
                            ? ''
                            : ' argus-st-section-filter-row--single'
                        }`}
                      >
                        {filteredDomainsOnIpList.length > 1 && (
                          <div className="argus-st-section-filter-search">
                            <PdsTextField
                              placeholder="Search within this section"
                              value={stSectionFilters.domainsOnIp}
                              onChange={(e) => {
                                setStSectionFilters((p) => ({ ...p, domainsOnIp: e.target.value }));
                                updatePagination('domainsOnIp', 1);
                              }}
                              size="sm"
                              stretch
                            />
                          </div>
                        )}
                        {domainsOnIpFilterOptions.length > 0 && (
                          <NativeSelect
                            size="sm"
                            classNames={{
                              root: 'argus-st-section-filter-select',
                              input: 'argus-native-select-input',
                            }}
                            value={domainsOnIpSiblingFilter}
                            onChange={(e) => {
                              setDomainsOnIpSiblingFilter(e.target.value);
                              updatePagination('domainsOnIp', 1);
                            }}
                            data={[
                              { value: 'ALL', label: 'All A and AAAA records' },
                              ...domainsOnIpFilterOptions,
                            ]}
                          />
                        )}
                      </div>
                    )}
                    {filteredDomainsOnIpList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : siblingFilteredDomainsOnIpList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match the selected A / AAAA record.</PdsMantineText>
                    ) : domainsOnIpDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(domainsOnIpDisplayList, 'domainsOnIp').map((domain, idx) => {
                      const lk = findLinkageForValue(domain.relatedDomain);
                      return (
                        <InvestigationDenseRow
                          key={idx}
                          dense={securityTrailsDense}
                          variant="row"
                          collapsible={false}
                          actions={showInvestigationPair ? (
                            <InvestigatePairButtons
                              targetDomain={domain.relatedDomain}
                              deriveFrom="DOMAIN_ON_IP"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.DOMAIN_ON_IP}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(domain.relatedDomain)}
                            />
                          ) : null}
                        >
                          <PdsBox direction="column" gap="xs" flex="1">
                            <PdsBox direction="row" alignItems="center" gap="xs">
                              <PdsMantineText type="textline" component="span" className="argus-data">
                                {domain.relatedDomain}
                              </PdsMantineText>
                            {lk && (
                                <PdsMantineIconButton size="sm" rank="ghost" color="action" icon={<PdsLinkIcon />} onClick={() => setLinkageModal({ open: true, data: lk })} aria-label="Show linkage" />
                              )}
                            </PdsBox>
                            <PdsBox direction="row" alignItems="baseline" gap="xs" flexWrap="wrap">
                              <PdsMantineText type="textline" component="span" className="argus-type-label">
                                IP:
                              </PdsMantineText>
                              <PdsMantineText type="textline" component="span" className="argus-data-sm">
                                {domain.targetIp}
                              </PdsMantineText>
                            </PdsBox>
                            <DomainsListMetadataFields row={domain} />
                          </PdsBox>
                        </InvestigationDenseRow>
                      );
                    })}
                    {domainsOnIpDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.domainsOnIp.currentPage} totalItems={domainsOnIpDisplayList.length} itemsPerPage={paginationState.domainsOnIp.itemsPerPage} onPageChange={(p) => updatePagination('domainsOnIp', p)} onPageSizeChange={(s) => updatePageSize('domainsOnIp', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}

              {/* Domains on Same NS */}
              {details.domainsOnNs?.length > 0 && (
                <InvestigationSectionCard
                  dense={securityTrailsDense}
                  accordionId="st-domains-ns"
                  expandedAccordionIds={securityTrailsOpenSectionIds}
                  onAccordionToggle={toggleSecurityTrailsSection}
                  title="Domains on Same NS"
                  icon={PdsSitemapIcon}
                  count={domainsOnNsDisplayList.length}
                >
                  <PdsBox direction="column" gap="xs">
                    {(domainsOnNsFilterOptions.length > 0 || filteredDomainsOnNsList.length > 1) && (
                      <div
                        className={`argus-st-section-filter-row${
                          filteredDomainsOnNsList.length > 1 && domainsOnNsFilterOptions.length > 0
                            ? ''
                            : ' argus-st-section-filter-row--single'
                        }`}
                      >
                        {filteredDomainsOnNsList.length > 1 && (
                          <div className="argus-st-section-filter-search">
                            <PdsTextField
                              placeholder="Search within this section"
                              value={stSectionFilters.domainsOnNs}
                              onChange={(e) => {
                                setStSectionFilters((p) => ({ ...p, domainsOnNs: e.target.value }));
                                updatePagination('domainsOnNs', 1);
                              }}
                              size="sm"
                              stretch
                            />
                          </div>
                        )}
                        {domainsOnNsFilterOptions.length > 0 && (
                          <NativeSelect
                            size="sm"
                            classNames={{
                              root: 'argus-st-section-filter-select',
                              input: 'argus-native-select-input',
                            }}
                            value={domainsOnNsNameserverFilter}
                            onChange={(e) => {
                              setDomainsOnNsNameserverFilter(e.target.value);
                              updatePagination('domainsOnNs', 1);
                            }}
                            data={[
                              { value: 'ALL', label: 'All nameservers' },
                              ...domainsOnNsFilterOptions,
                            ]}
                          />
                        )}
                      </div>
                    )}
                    {filteredDomainsOnNsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match your search.</PdsMantineText>
                    ) : nameserverFilteredDomainsOnNsList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match the selected nameserver.</PdsMantineText>
                    ) : domainsOnNsDisplayList.length === 0 ? (
                      <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">No rows match this card filter.</PdsMantineText>
                    ) : getPaginatedItems(domainsOnNsDisplayList, 'domainsOnNs').map((domain, idx) => {
                      const lk = findLinkageForValue(domain.relatedDomain);
                      return (
                        <InvestigationDenseRow
                          key={idx}
                          dense={securityTrailsDense}
                          variant="row"
                          collapsible={false}
                          actions={showInvestigationPair ? (
                            <InvestigatePairButtons
                              targetDomain={domain.relatedDomain}
                              deriveFrom="DOMAIN_ON_NS"
                              connectionLabel={SECURITY_TRAILS_CARD_EDGE_LABEL.DOMAIN_ON_NS}
                              onRunTools={handleInvestigate}
                              onAddNodeOnly={pairAddNodeOnly}
                              showAddNode={!isTargetAlreadyOnCaseMap(domain.relatedDomain)}
                            />
                          ) : null}
                        >
                          <PdsBox direction="column" gap="xs" flex="1">
                            <PdsBox direction="row" alignItems="center" gap="xs">
                              <PdsMantineText type="textline" component="span" className="argus-data">{domain.relatedDomain}</PdsMantineText>
                              {lk && (
                                <PdsMantineIconButton size="sm" rank="ghost" color="action" icon={<PdsLinkIcon />} onClick={() => setLinkageModal({ open: true, data: lk })} aria-label="Show linkage" />
                              )}
                            </PdsBox>
                            <PdsBox direction="row" alignItems="baseline" gap="xs" flexWrap="wrap">
                              <PdsMantineText type="textline" component="span" className="argus-type-label">
                                NS:
                              </PdsMantineText>
                              <PdsMantineText type="textline" component="span" className="argus-data-sm">
                                {domain.nameserver}
                              </PdsMantineText>
                            </PdsBox>
                            <DomainsListMetadataFields row={domain} />
                          </PdsBox>
                        </InvestigationDenseRow>
                      );
                    })}
                    {domainsOnNsDisplayList.length > 0 && (
                    <Pagination currentPage={paginationState.domainsOnNs.currentPage} totalItems={domainsOnNsDisplayList.length} itemsPerPage={paginationState.domainsOnNs.itemsPerPage} onPageChange={(p) => updatePagination('domainsOnNs', p)} onPageSizeChange={(s) => updatePageSize('domainsOnNs', s)} />
                    )}
                  </PdsBox>
                </InvestigationSectionCard>
              )}
            </InvestigationSectionGrid>
            </>
              )}
            </>
          )}

          {activeSource === 'securitytrails-historical' && (
            <div data-section-id="tab-securitytrails-historical">
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'SECURITY_TRAILS')}
                streamingPreview={streamingPreview}
              />
              {toolNotRunCaseCopy('enableSecurityTrails') ? (
                <PdsMantineText type="body" color="neutral" style={{ marginBottom: 8, lineHeight: 1.55 }} component="p">
                  SecurityTrails was not run for this node. Use <strong>Re-run tools</strong> to include historical DNS data.
                </PdsMantineText>
              ) : !hasSecurityTrailsHistoricalTab ? (
                <PdsMantineText type="body" color="neutral" style={{ fontStyle: 'italic', marginBottom: 8 }} component="p">
                  No historical DNS rows are stored for this node yet. If a message appears above, the lookup returned no data or failed for your query.
                </PdsMantineText>
              ) : (
                <SecurityTrailsHistoricalPanel
                  details={details}
                  domain={details.targetValue}
                  keywordFilteredRecords={filteredHistoricalDnsRecordsList}
                  onRunTools={handleInvestigate}
                  onAddNodeOnly={pairAddNodeOnly}
                  showInvestigationPair={showInvestigationPair}
                  isTargetAlreadyOnCaseMap={isTargetAlreadyOnCaseMap}
                />
              )}
            </div>
          )}

          {/* UrlScan */}
          {activeSource === 'urlscan' && isPreviewMode && details.urlScanPreviewRows != null && (
            <PdsBox direction="column" gap="sm" data-section-id="tab-urlscan">
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'URL_SCAN_SCAN')}
                streamingPreview={streamingPreview}
              />
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'URL_SCAN_SEARCH')}
                streamingPreview={streamingPreview}
              />
              <UrlScanInformation
                preloadedRows={details.urlScanPreviewRows}
                investigationTarget={details?.targetValue}
                emptyHint={urlScanEmptyHint}
                onInvestigate={previewContinueActive ? (d, df, _cl) => handleInvestigate(d, df, undefined, { launchFrom: 'urlscan' }) : undefined}
                viewDensity={urlScanViewDensity}
              />
            </PdsBox>
          )}
          {activeSource === 'urlscan' && !isPreviewMode && (
            <div data-section-id="tab-urlscan">
            <UrlScanInformation
              investigationId={investigationId}
              investigationTarget={details?.targetValue}
              onInvestigate={(d, df, cl) => handleInvestigate(d, df, cl, { launchFrom: 'urlscan' })}
              onAddNodeOnly={(d, df, cl) => handleAddPlaceholderNode(d, df, cl)}
              caseMapHasTarget={isTargetAlreadyOnCaseMap}
              keywordFilters={keywordFilterQuery}
              viewDensity={urlScanViewDensity}
              emptyHint={urlScanEmptyHint}
              caseInvestigationView
              workflowRequestedTools={workflowFlags}
              investigationTerminal={investigationTerminal}
            />
            </div>
          )}

          {/* PublicWWW */}
          {activeSource === 'publicwww' && isPreviewMode && showPublicWwwTab && (
            <PdsBox direction="column" gap="sm" data-section-id="tab-publicwww">
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'PUBLIC_WWW')}
                streamingPreview={streamingPreview}
              />
              {details.publicWwwResults && details.publicWwwResults.length > 0 ? (
                <PublicWwwPreviewTable
                  rows={sortPublicWwwByRankAsc(details.publicWwwResults)}
                  searchTerm={details.targetValue}
                  onPreviewContinue={previewContinueActive ? handleInvestigate : undefined}
                />
              ) : (
                <PdsMantineText type="body" color="neutral" style={{ margin: 0, lineHeight: 1.55 }}>
                  Quick preview here covers SecurityTrails, URL Scan and Crt.sh only. PublicWWW is available as an additional search tool after saving as a case.
                </PdsMantineText>
              )}
            </PdsBox>
          )}
          {activeSource === 'publicwww' && !isPreviewMode && (
            <div data-section-id="tab-publicwww">
            <UrlScanInformation
              variant="publicwww"
              investigationId={investigationId}
              onInvestigate={handleInvestigate}
              onAddNodeOnly={handleAddPlaceholderNode}
              caseMapHasTarget={isTargetAlreadyOnCaseMap}
              keywordFilters={keywordFilterQuery}
              highlightTerm={details?.targetValue}
              viewDensity={publicWwwViewDensity}
              emptyHint={publicWwwEmptyHint}
              caseInvestigationView
              workflowRequestedTools={workflowFlags}
              investigationTerminal={investigationTerminal}
              onRunPublicWww={handleRunPublicWww}
              publicWwwRunning={publicWwwRunning}
              dataRefreshNonce={publicWwwRefreshNonce}
            />
            </div>
          )}

          {/* Certificate Transparency (crt.sh) */}
          {activeSource === 'crtsh' && showCrtTab && (
            <div data-section-id="tab-crtsh">
            <PdsBox direction="column" gap="sm">
              <InvestigationToolOutcomeBanner
                outcome={toolOutcomeByKind(details.toolOutcomes, 'CRT_SH')}
                streamingPreview={streamingPreview}
              />
              {toolNotRunCaseCopy('enableCrtSh') ? (
                <PdsMantineText type="body" color="neutral" style={{ lineHeight: 1.55 }} component="p">
                  Certificate Transparency (crt.sh) was not run for this node. Use <strong>Re-run tools</strong>{' '}
                  to include it.
                </PdsMantineText>
              ) : (
                <>
              {!isPreviewMode && (details.certificates?.length || 0) > 0 && (
                <PdsBox
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  wrap="wrap"
                  gap="sm"
                  style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e8eaed' }}
                >
                  <PdsMantineText type="textline" fontWeight="strong" color="neutral" component="span">
                    Certificate status:
                  </PdsMantineText>
                  {[
                    { id: 'ALL', label: 'All', count: certificateStatusCounts.all },
                    { id: 'EXPIRED', label: 'Expired', count: certificateStatusCounts.expired },
                    { id: 'EXPIRING', label: 'Expiring', count: certificateStatusCounts.expiring },
                    { id: 'VALID', label: 'Valid', count: certificateStatusCounts.valid },
                  ].map(({ id, label, count }) => (
                    <PdsMantineButton
                      key={id}
                      rank={crtFilter === id ? 'fill' : 'outline'}
                      color={crtFilter === id ? 'action' : 'neutral'}
                      size="xs"
                      onClick={() => setCrtFilter(id)}
                    >
                      {label} ({count})
                    </PdsMantineButton>
                  ))}
                  {expandableSanCertIds.length > 0 && (
                    <PdsMantineButton
                      rank="outline"
                      color="neutral"
                      size="xs"
                      onClick={toggleAllCertSans}
                      leadingIcon={allSansExpanded ? <PdsChevronRightSmIcon /> : <PdsChevronDownSmIcon />}
                      aria-pressed={allSansExpanded}
                      style={{ marginLeft: 'auto' }}
                    >
                      {allSansExpanded ? 'Collapse all SANs' : 'Expand all SANs'}
                    </PdsMantineButton>
                  )}
                </PdsBox>
              )}
              {(details.certificates?.length || 0) === 0 ? (
                showCrtShRerunPrompt ? (
                  <CrtShRerunPrompt onRerun={handleRerunCrtSh} rerunning={crtShRerunning} />
                ) : (
                <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                  No certificate transparency results stored for &quot;{details.targetValue}&quot;. If a message appears above,
                  the crt.sh lookup may have failed or returned no certificates for your query.
                </PdsMantineText>
                )
              ) : filteredCertificates.length === 0 ? (
                <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                  No certificates match this filter.
                </PdsMantineText>
              ) : certificatesForDisplay.length === 0 ? (
                <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                  No certificates match your search.
                </PdsMantineText>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
                  {getPaginatedItems(certificatesForDisplay, 'certificates').map((cert) => {
                    const expired = cert.notAfter && new Date(cert.notAfter) < new Date();
                    const expiringSoon = !expired && cert.notAfter && new Date(cert.notAfter) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    const title = cert.commonName || cert.domain;
                    const certTargetForMap = title;
                    const statusTags = (
                      <PdsBox direction="row" gap="xs" alignItems="center">
                        {expired && <PdsTag color="attention" dismissible={false} size="xs">EXPIRED</PdsTag>}
                        {!expired && expiringSoon && <PdsTag color="warning" dismissible={false} size="xs">EXPIRING</PdsTag>}
                      </PdsBox>
                    );
                    const investigateBtn = showInvestigationPair ? (
                      <InvestigatePairButtons
                        targetDomain={certTargetForMap}
                        deriveFrom="CERTIFICATE"
                        onRunTools={handleInvestigate}
                        onAddNodeOnly={pairAddNodeOnly}
                        showAddNode={!isTargetAlreadyOnCaseMap(certTargetForMap)}
                      />
                    ) : null;
                    const detailsBlock = (
                      <PdsBox direction="column" gap="xs">
                        {cert.commonName && (
                          <PdsMantineText type="textline" component="span">
                            <strong>Common Name:</strong> <span className="argus-data-sm" style={{ wordBreak: 'break-all' }}>{cert.commonName}</span>
                          </PdsMantineText>
                        )}
                        {cert.certId && (
                          <PdsMantineText type="textline" component="span">
                            <strong>Cert ID:</strong> <span className="argus-data-sm">{cert.certId}</span>
                          </PdsMantineText>
                        )}
                        {cert.serialNumber && (
                          <PdsMantineText type="textline" component="span">
                            <strong>Serial:</strong> <span className="argus-data-sm">{cert.serialNumber}</span>
                          </PdsMantineText>
                        )}
                        <PdsMantineText type="textline" component="span"><strong>Issuer:</strong> {cert.issuerName || 'Unknown'}</PdsMantineText>
                        <PdsMantineText type="textline" component="span">
                          <strong>First Seen:</strong> {cert.notBefore ? new Date(cert.notBefore).toLocaleDateString() : 'N/A'}
                          {' · '}
                          <strong>Last Seen:</strong> {cert.notAfter ? new Date(cert.notAfter).toLocaleDateString() : 'N/A'}
                        </PdsMantineText>
                        <PdsMantineText type="textline" component="span"><strong>SANs:</strong> {cert.subjectAltNames?.length || 0} &nbsp; <strong>Source:</strong> {cert.source}</PdsMantineText>
                      </PdsBox>
                    );
                    const sansOpen = expandedSanCertIds.has(cert.id);
                    const sansBlock = cert.subjectAltNames?.length > 0 && (
                      <div>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-expanded={sansOpen}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => toggleCertSans(cert.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleCertSans(cert.id);
                            }
                          }}
                        >
                          <PdsMantineText type="textline" color="action" component="span">
                            {sansOpen ? '▾ Hide' : '▸ View'} SANs ({cert.subjectAltNames.length})
                          </PdsMantineText>
                        </span>
                        {sansOpen && (
                          <PdsBox direction="column" gap="xs" style={{ marginTop: 8, maxHeight: 150, overflowY: 'auto' }}>
                            {cert.subjectAltNames.map((san, idx) => (
                              <PdsBox key={idx} direction="row" justifyContent="space-between" alignItems="center" gap="sm">
                                <PdsMantineText type="textline" component="span" className="argus-data-sm" style={{ wordBreak: 'break-all', flex: 1 }}>{san.sanValue}</PdsMantineText>
                                {showInvestigationPair && (
                                <InvestigatePairButtons
                                  targetDomain={san.sanValue}
                                  deriveFrom="SANS"
                                  onRunTools={handleInvestigate}
                                  onAddNodeOnly={pairAddNodeOnly}
                                  showAddNode={!isTargetAlreadyOnCaseMap(san.sanValue)}
                                />
                                )}
                              </PdsBox>
                            ))}
                          </PdsBox>
                        )}
                      </div>
                    );
                    if (crtDense) {
                      return (
                        <InvestigationDenseRow
                          key={cert.id}
                          dense
                          variant="column"
                          titleLine={title}
                          actions={(
                            <PdsBox direction="row" gap="xs" alignItems="center">
                              {statusTags}
                              {investigateBtn}
                            </PdsBox>
                          )}
                        >
                          <PdsBox direction="column" gap="sm">
                            {detailsBlock}
                            {sansBlock}
                          </PdsBox>
                        </InvestigationDenseRow>
                      );
                    }
                    return (
                      <PdsCard
                        key={cert.id}
                        border
                        borderRadius="sm"
                        padding="sm"
                        direction="column"
                        gap="sm"
                        color={expired ? 'attention' : expiringSoon ? 'warning' : 'neutral'}
                        contrast="low"
                      >
                        <PdsBox direction="row" justifyContent="space-between" alignItems="center" gap="xs" wrap="wrap">
                          <PdsMantineText type="textline" fontWeight="strong" component="span">{title}</PdsMantineText>
                          <PdsBox direction="row" gap="xs" alignItems="center">
                            {statusTags}
                            {investigateBtn}
                          </PdsBox>
                        </PdsBox>
                        {detailsBlock}
                        {sansBlock}
                      </PdsCard>
                    );
                  })}
                </div>
              )}
              <Pagination currentPage={paginationState.certificates.currentPage} totalItems={certificatesForDisplay.length} itemsPerPage={paginationState.certificates.itemsPerPage} onPageChange={(p) => updatePagination('certificates', p)} onPageSizeChange={(s) => updatePageSize('certificates', s)} />
                </>
              )}
            </PdsBox>
            </div>
          )}

          {/* Findings — case page only; empty state explains Add Finding */}
          {showFindingsTab && activeSource === 'manual' && !(otherInformation?.length > 0) && (
            <PdsCard border borderRadius="md" padding="md" direction="column" gap="sm">
              {FINDINGS_TAB_EMPTY_EXPLAINER}
            </PdsCard>
          )}

          {/* Findings (grouped by category) */}
          {showFindingsTab && activeSource === 'manual' && otherInformation?.length > 0 && (
            <PdsBox direction="column" gap="md">
              {!isPreviewMode && (
                <PdsBox
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  wrap="wrap"
                  gap="sm"
                  style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e8eaed' }}
                >
                  <PdsMantineText type="textline" fontWeight="strong" color="neutral" component="span">
                    Finding types:
                  </PdsMantineText>
                  <PdsMantineButton
                    rank={findingTypeFilter === 'ALL' ? 'fill' : 'outline'}
                    color={findingTypeFilter === 'ALL' ? 'action' : 'neutral'}
                    size="xs"
                    onClick={() => setFindingTypeFilter('ALL')}
                  >
                    All ({sortedFindings.length})
                  </PdsMantineButton>
                  {findingTypes.map((t) => {
                    const count = sortedFindings.filter((f) => f.informationType === t).length;
                    const label = FINDING_TYPE_EDGE_LABEL[t] || t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
                    return (
                      <PdsMantineButton
                        key={t}
                        rank={findingTypeFilter === t ? 'fill' : 'outline'}
                        color={findingTypeFilter === t ? 'action' : 'neutral'}
                        size="xs"
                        onClick={() => setFindingTypeFilter(t)}
                      >
                        {label} ({count})
                      </PdsMantineButton>
                    );
                  })}
                </PdsBox>
              )}
              {filteredFindings.length === 0 ? (
                <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                  No findings match this filter.
                </PdsMantineText>
              ) : findingsForDisplay.length === 0 ? (
                <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                  No findings match your search.
                </PdsMantineText>
              ) : (
              <>
              {FINDINGS_TAB_LIST_EXPLAINER}
              <PdsBox direction="column" gap="sm" style={{ maxWidth: 960 }}>
                {getPaginatedItems(findingsForDisplay, 'manualFindings').map((info, idx, arr) => {
                  const prev = idx > 0 ? arr[idx - 1] : null;
                  const showCat =
                    !prev
                    || prev.informationType !== info.informationType
                    || (info.informationType === 'CUSTOM'
                      && (prev.customCategory || '') !== (info.customCategory || ''));
                  const catLabel = resolveFindingCategoryHeading(info);
                  const lk = findLinkageForValue(info.informationValue);
                  const findingTargetRaw = info.informationValue;
                  const findingTargetTrimmed = findingTargetRaw != null ? String(findingTargetRaw).trim() : '';
                  const showAddFindingToMap = canShowAddFindingToMap({
                    finding: info,
                    investigationId,
                    caseInvestigations,
                    caseGraphLinks,
                  });
                  const findingAllowsToolInvestigation = FINDING_TYPES_FULL_INVESTIGATE.includes(info.informationType);
                  const actionRow = (
                    <PdsBox direction="row" justifyContent="flex-end" alignItems="center" gap="xs" wrap="wrap">
                      {findingTargetTrimmed && !isPreviewMode && (
                        <InvestigatePairButtons
                          targetDomain={findingTargetTrimmed}
                          deriveFrom={info.informationType === 'CUSTOM' ? 'MANUAL_FINDING' : info.informationType}
                          connectionLabel={connectionLabelFromFindingRow(info)}
                          onRunTools={handleInvestigate}
                          onAddNodeOnly={handleAddPlaceholderNode}
                          showInvestigate={findingAllowsToolInvestigation}
                          showAddNode={showAddFindingToMap}
                          buttonSize="sm"
                        />
                      )}
                      <PdsMantineButton rank="outline" color="neutral" size="xs" onClick={() => { setEditingFinding(info); setShowEditFindingModal(true); }} leadingIcon={<PdsEditIcon />}>
                        Edit
                      </PdsMantineButton>
                      <PdsMantineButton rank="outline" color="attention" size="xs" onClick={() => handleDeleteOtherInformation(info.id)} leadingIcon={<PdsTrashDeleteIcon />}>
                        Delete
                      </PdsMantineButton>
                    </PdsBox>
                  );
                  const body = (
                    <>
                      {!manualDense && actionRow}
                      <PdsBox direction="row" alignItems="center" gap="xs">
                        <PdsMantineText
                          type="textline"
                          component="span"
                          className="argus-data-sm"
                          style={{ wordBreak: 'break-all', flex: 1 }}
                        >
                          {truncateUrl(info.informationValue)}
                        </PdsMantineText>
                        {lk && (
                          <PdsMantineIconButton size="sm" rank="ghost" color="action" icon={<PdsLinkIcon />} onClick={() => setLinkageModal({ open: true, data: lk })} aria-label="Show linkage" />
                        )}
                      </PdsBox>
                      {info.remarks && (
                        <PdsBox barPosition="left" barColor="neutral" barSize="sm" padding="xs" color="neutral" contrast="low" borderRadius="sm">
                          <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">{info.remarks}</PdsMantineText>
                        </PdsBox>
                      )}
                      <PdsMantineText type="textline" color="neutral" component="span">
                        Added: {new Date(info.createdAt).toLocaleString()}
                      </PdsMantineText>
                    </>
                  );
                  return (
                    <React.Fragment key={info.id}>
                      {showCat && (
                        <PdsMantineText
                          type="textline"
                          fontWeight="strong"
                          component="span"
                          style={{ marginTop: idx > 0 ? 16 : 0, paddingBottom: 6, borderBottom: '1px solid #e1e4e8' }}
                        >
                          {catLabel}
                        </PdsMantineText>
                      )}
                      {manualDense ? (
                        <div data-finding-id={info.id}>
                          <InvestigationDenseRow
                            dense
                            variant="column"
                            titleLine={truncateUrl(info.informationValue)}
                            actions={actionRow}
                          >
                            {body}
                          </InvestigationDenseRow>
                        </div>
                      ) : (
                        <div data-finding-id={info.id}>
                          <PdsCard border borderRadius="sm" padding="sm" direction="column" gap="sm" color="neutral" contrast="low">
                            {body}
                          </PdsCard>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </PdsBox>
              <Pagination currentPage={paginationState.manualFindings.currentPage} totalItems={findingsForDisplay.length} itemsPerPage={paginationState.manualFindings.itemsPerPage} onPageChange={(p) => updatePagination('manualFindings', p)} onPageSizeChange={(s) => updatePageSize('manualFindings', s)} />
              </>
              )}
            </PdsBox>
          )}

        </PdsBox>
      </PdsBox>

      {/* Add Finding Modal */}
      {showAddFindingModal && (
        <AddFindingModal
          investigationId={investigationId}
          targetDomain={details?.targetValue}
          onClose={() => setShowAddFindingModal(false)}
          onSuccess={async () => {
            setShowAddFindingModal(false);
            setActiveSource('manual');
            setSuccessMessage('Finding added successfully');
            await refreshFindingsAndNetwork();
          }}
        />
      )}

      {showExportModal && (
        <InvestigationExportModal
          isOpen={showExportModal}
          investigationId={investigationId}
          targetDomain={details?.targetValue}
          onClose={() => setShowExportModal(false)}
          onError={(message) => setExportError(message)}
        />
      )}

      {/* Investigation Modal */}
      <InvestigationModal
        isOpen={showInvestigationModal}
        onClose={() => { setShowInvestigationModal(false); setIsRerunMode(false); setDeriveFromValue(null); setInvestigateLaunchContext(null); }}
        onInvestigate={handleInvestigationWithTools}
        targetDomain={targetDomainForInvestigation}
        isRerunMode={isRerunMode}
      />

      {/* Edit Finding Modal */}
      {showEditFindingModal && editingFinding && (
        <EditFindingModal
          finding={editingFinding}
          onClose={() => { setShowEditFindingModal(false); setEditingFinding(null); }}
          onSuccess={async () => {
            setShowEditFindingModal(false);
            setEditingFinding(null);
            setSuccessMessage('Finding updated successfully');
            await refreshFindingsAndNetwork();
          }}
        />
      )}

      {/* Linkage Modal */}
      <PdsModal
        show={linkageModal.open}
        onDismiss={() => setLinkageModal({ open: false, data: null })}
        minWidth="400px"
        maxWidth="500px"
        minHeight="auto"
        maxHeight="80vh"
      >
        <PdsModalHeader headline="Cross-Case Linkage" />
        <PdsModalBody>
          {linkageModal.data && (
            <CrossCaseLinkageRecord
              link={linkageModal.data}
              podium
              onNavigate={() => setLinkageModal({ open: false, data: null })}
              style={{ border: 'none', padding: 0, background: 'transparent' }}
            />
          )}
        </PdsModalBody>
      </PdsModal>

    </PdsBox>
  );
};

// ─── AddFindingModal ──────────────────────────────────────────────────────────

const INFORMATION_TYPES = [
  { value: 'OPERATING_COMPANY', label: 'Operating Company' },
  { value: 'CONTACT_DETAILS',   label: 'Contact Details' },
  { value: 'RELATED_DOMAIN',    label: 'Related Domain' },
  { value: 'NETWORK_TRAFFIC',   label: 'Network Traffic' },
  { value: 'SOURCE_CODES',      label: 'Source Codes' },
  { value: 'LINKS',             label: 'Links' },
  { value: 'CUSTOM',            label: 'Custom' },
];

const CUSTOM_CHART_CATEGORY_HELPER =
  'Name the type of item this finding represents on the node map. Use a short category label, not the specific finding value.';

const FINDING_CUSTOM_CATEGORY_EXAMPLE_ROWS = [
  {
    category: 'Mirror domain',
    information: 'skyexch.vip',
    remarks: 'Shares layout and infrastructure indicators with target site',
  },
  {
    category: 'Operator alias',
    information: 'SingBet',
    remarks: 'Name used across related betting properties',
  },
  {
    category: 'Payment processor',
    information: 'ExamplePay',
    remarks: 'Appears in checkout flow',
  },
  {
    category: 'Hosting provider',
    information: 'Cloudflare',
    remarks: 'Observed from DNS and HTTP headers',
  },
  {
    category: 'Telegram channel',
    information: '@examplechannel',
    remarks: 'Used for user acquisition or support',
  },
];

function FindingCustomCategoryExamplesTable() {
  return (
    <PdsBox direction="column" gap="xs">
      <PdsMantineText type="textline" fontWeight="strong" component="p" style={{ margin: 0 }}>
        Examples:
      </PdsMantineText>
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid var(--pds-colors-neutral-border-base, #e5e7eb)',
          borderRadius: 8,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <thead>
            <tr style={{ borderBottom: '1px solid var(--pds-colors-neutral-border-base, #e5e7eb)', background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Custom chart category</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Information</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {FINDING_CUSTOM_CATEGORY_EXAMPLE_ROWS.map((row) => (
              <tr
                key={row.category + row.information}
                style={{ borderBottom: '1px solid var(--pds-colors-neutral-border-base, #e5e7eb)' }}
              >
                <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{row.category}</td>
                <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{row.information}</td>
                <td style={{ padding: '10px 12px', verticalAlign: 'top', color: '#475569' }}>{row.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PdsBox>
  );
}

const AddFindingModal = ({ investigationId, targetDomain, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    informationType: 'OPERATING_COMPANY',
    informationValue: '',
    remarks: '',
    customCategory: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!formData.informationValue.trim()) { setError('Information value is required'); return; }
    if (formData.informationType === 'CUSTOM' && !formData.customCategory.trim()) {
      setError('Enter a custom chart category');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        informationType: formData.informationType,
        informationValue: formData.informationValue.trim(),
        remarks: formData.remarks || undefined,
      };
      if (formData.informationType === 'CUSTOM') {
        body.customCategory = formData.customCategory.trim();
      }
      await axios.post(`/api/investigations/${investigationId}/other-information`, body);
      onSuccess();
    } catch (e) {
      setError('Failed to add finding: ' + (e.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdsModal show onDismiss={onClose} minWidth="460px" maxWidth="640px" minHeight="auto" maxHeight="90vh">
      <PdsModalHeader
        headline={
          targetDomain?.trim()
            ? `Add Finding - ${targetDomain.trim()}`
            : 'Add Finding'
        }
      />
      <PdsModalBody>
        <PdsBox direction="column" gap="md" style={{ width: '100%' }}>
          <NativeSelect
            label="Type"
            classNames={{ input: 'argus-native-select-input' }}
            value={formData.informationType}
            onChange={(e) => setFormData({
              ...formData,
              informationType: e.target.value,
              customCategory: e.target.value === 'CUSTOM' ? formData.customCategory : '',
            })}
            data={INFORMATION_TYPES}
            style={{ width: '100%' }}
          />
          {formData.informationType === 'CUSTOM' && (
            <>
              <PdsTextField
                label="Custom chart category"
                value={formData.customCategory}
                onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                maxLength={160}
                required
                stretch
                helperTextMessage={CUSTOM_CHART_CATEGORY_HELPER}
                helperTextType="neutral"
              />
              <FindingCustomCategoryExamplesTable />
            </>
          )}
          <PdsTextField
            label="Information"
            placeholder="Enter the finding information..."
            value={formData.informationValue}
            onChange={(e) => setFormData({ ...formData, informationValue: e.target.value })}
            maxLength={255}
            required
            stretch
            helperTextMessage="Maximum 255 characters"
            helperTextType="neutral"
          />
          <PdsTextArea
            label="Remarks"
            placeholder="Additional description or notes about this finding..."
            value={formData.remarks}
            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            stretch
            rows={4}
          />
          {error && <PdsNotification color="warning" dismissible={false}>{error}</PdsNotification>}
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton rank="outline" color="neutral" onClick={onClose} disabled={submitting}>Cancel</PdsMantineButton>
          <PdsMantineButton rank="fill" color="action" onClick={handleSubmit} disabled={submitting || !formData.informationValue.trim() || (formData.informationType === 'CUSTOM' && !formData.customCategory.trim())} leadingIcon={submitting ? <PdsSpinnerIcon /> : <PdsCheckConfirmIcon />}>
            {submitting ? 'Saving...' : 'Save Finding'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
};

// ─── EditFindingModal ─────────────────────────────────────────────────────────

const EditFindingModal = ({ finding, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    informationType: finding.informationType || 'LINKS',
    informationValue: finding.informationValue || '',
    remarks: finding.remarks || '',
    customCategory: finding.customCategory || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (formData.informationType === 'CUSTOM' && !formData.customCategory.trim()) {
      setError('Enter a custom chart category');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        informationType: formData.informationType,
        informationValue: formData.informationValue.trim(),
        remarks: formData.remarks || undefined,
      };
      if (formData.informationType === 'CUSTOM') {
        body.customCategory = formData.customCategory.trim();
      }
      const response = await axios.put(`/api/investigations/other-information/${finding.id}`, body);
      onSuccess(response.data);
    } catch (err) {
      setError('Failed to update finding: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdsModal show onDismiss={onClose} minWidth="460px" maxWidth="640px" minHeight="auto" maxHeight="90vh">
      <PdsModalHeader headline="Edit Finding" />
      <PdsModalBody>
        <PdsBox direction="column" gap="md" style={{ width: '100%' }}>
          {error && <PdsNotification color="warning" dismissible={false}>{error}</PdsNotification>}
          <NativeSelect
            label="Type"
            classNames={{ input: 'argus-native-select-input' }}
            value={formData.informationType}
            onChange={(e) => setFormData({
              ...formData,
              informationType: e.target.value,
              customCategory: e.target.value === 'CUSTOM' ? formData.customCategory : '',
            })}
            data={INFORMATION_TYPES}
            style={{ width: '100%' }}
          />
          {formData.informationType === 'CUSTOM' && (
            <>
              <PdsTextField
                label="Custom chart category"
                value={formData.customCategory}
                onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                maxLength={160}
                required
                stretch
                helperTextMessage={CUSTOM_CHART_CATEGORY_HELPER}
                helperTextType="neutral"
              />
              <FindingCustomCategoryExamplesTable />
            </>
          )}
          <PdsTextArea
            label="Information"
            placeholder="Enter the finding information..."
            value={formData.informationValue}
            onChange={(e) => setFormData({ ...formData, informationValue: e.target.value })}
            required
            stretch
            rows={4}
          />
          <PdsTextArea
            label="Remarks"
            placeholder="Additional description or notes about this finding..."
            value={formData.remarks}
            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            stretch
            rows={4}
          />
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton rank="outline" color="neutral" onClick={onClose} disabled={submitting}>Cancel</PdsMantineButton>
          <PdsMantineButton rank="fill" color="action" onClick={handleSubmit} disabled={submitting || !formData.informationValue.trim() || (formData.informationType === 'CUSTOM' && !formData.customCategory.trim())} leadingIcon={submitting ? <PdsSpinnerIcon /> : <PdsCheckConfirmIcon />}>
            {submitting ? 'Updating...' : 'Update Finding'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
};

export default CaseDetails;
